// Centralised help content registry, keyed by route path.
// Each entry maps to the structured help data displayed in the HelpDrawer.

import type React from 'react';
import type { LucideProps } from 'lucide-react';
import {
  Home,
  Clock,
  CheckCircle2,
  FileText,
  AlertTriangle,
  TrendingUp,
  Users,
  Briefcase,
  Zap,
  Settings,
  Bell,
  LayoutDashboard,
  Shield,
  ClipboardCheck,
  ArrowLeftRight,
} from 'lucide-react';

export interface HelpKeyFeature {
  label: string;
  detail: string;
}

export interface HelpSectionField {
  name: string;
  description: string;
}

export interface HelpStepItem {
  text?: string;
  note?: string;
  tip?: string;
  subSteps?: string[];
}

export interface HelpStep {
  title: string;
  titleAlign?: 'left' | 'center';
  items: HelpStepItem[];
}

export interface HelpFaqItem {
  question: string;
  answer: string;
  steps?: string[];
}

export interface HelpRelatedPage {
  label: string;
  path: string;
}

export type HelpIcon = React.ComponentType<LucideProps>;

export interface HelpContent {
  pageTitle: string;
  description: string;
  icon?: HelpIcon;
  functionality: {
    overview: string;
    keyFeatures: HelpKeyFeature[];
    sectionsAndFields: HelpSectionField[];
    useCases: string[];
  };
  userGuide: {
    gettingStarted: string;
    steps: HelpStep[];
    proTips: string[];
  };
  faq: HelpFaqItem[];
  relatedPages: HelpRelatedPage[];
}

export const helpContent: Record<string, HelpContent> = {
  '/': {
    pageTitle: 'Homepage',
    description: 'Your personal overview of your timesheet status, alerts, and quick access to key actions.',
    icon: Home,
    functionality: {
      overview:
        'The Homepage is the first screen you see after logging in. It gives you a personal snapshot of your current timesheet, any alerts requiring your attention, and quick access to view or submit your hours.',
      keyFeatures: [
        { label: 'This Week', detail: 'Shows your current week\'s hours, submission status, and a button to open your full timesheet — all at a glance.' },
        { label: 'Action Required', detail: 'Surfaces critical alerts such as overdue timesheets, rejection notices, and upcoming submission deadlines so you don\'t miss any important actions.' },
        { label: 'Recent Activity', detail: 'A feed of your latest timesheet submissions, approvals, and rejections to keep you up to date with past weeks.' },
        { label: 'Performance Metrics', detail: 'A row of metric cards showing your billable hours, this month\'s total hours, average weekly hours, and your personal utilisation rate.' },
        { label: 'My Utilisation', detail: 'Displays your current billable utilisation rate as a percentage, with a trend indicator compared to the previous period.' },
        { label: 'Submission Consistency', detail: 'A summary of how consistently you have submitted timesheets on time across recent weeks.' },
        { label: 'Time Allocation', detail: 'A breakdown of your logged hours across your top projects over a selected time period.' },
        { label: 'Insights & Trends', detail: 'AI-generated observations about your workload, utilisation, and submission behaviour to help you stay on track.' },
      ],
      sectionsAndFields: [
        { name: 'This Week', description: 'A summary card showing your logged hours for the current week, your submission status, and a button to open your full timesheet.' },
        { name: 'Action Required', description: 'Highlights any overdue timesheets, rejection notices, or upcoming submission deadlines that need your immediate attention.' },
        { name: 'Recent Activity', description: 'A feed of your latest timesheet events — submissions, approvals, and rejections — so you can stay up to date with past weeks.' },
        { name: 'Performance Metrics', description: 'A row of metric cards covering your billable hours, this month\'s total, average weekly hours, and your personal utilisation rate.' },
        { name: 'Submission Consistency', description: 'A chart or summary showing how consistently you have submitted timesheets on time over recent weeks.' },
        { name: 'Time Allocation', description: 'A breakdown of how your logged hours are distributed across your top projects over a selected time period.' },
        { name: 'Insights & Trends', description: 'AI-generated observations about your workload, utilisation, and submission behaviour to help you stay on track.' },
      ],
      useCases: [
        'Checking whether you have submitted your timesheet for the current week.',
        'Checking whether a previously submitted timesheet has been approved or rejected.',
        'Acting on an alert about an overdue or rejected timesheet directly from the homepage.',
        'Jumping straight into your timesheet grid to review and update your entries for the week.',
        'Reviewing insights about my time and submissions.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Your homepage gives you a snapshot of your current week. Check the Action Required card first — if anything needs your attention it will show here. From here you can also navigate straight to your timesheet using the View Timesheet button.',
      steps: [
        {
          title: 'How do I submit my timesheet from the homepage?',
          items: [
            { text: 'Locate the My Timesheet card on the homepage.' },
            { text: 'Click View Timesheet to open the full timesheet grid.', tip: 'If your timesheet is already complete, the Submit button may appear directly on the summary card.' },
            { text: 'Review your entries and click Submit for Approval.' },
          ],
        },
        {
          title: 'How do I open my timesheet from the homepage?',
          items: [
            { text: 'Locate the My Timesheet card on the homepage.' },
            { text: 'Click the View Timesheets button to open the full timesheet grid.' },
            { text: 'Add or edit entries as needed, then click Submit for Approval when ready.', tip: 'Any changes you make are saved automatically as you go — you do not need to save before navigating away.' },
          ],
        },
      ],
      proTips: [
        'Use View Timesheets from the homepage as the quickest way to get to your timesheet — it takes you straight to the current week.',
        'If your timesheet is rejected, the homepage alert will tell you which week is affected — click through to see the reviewer\'s comment.',
        'If your timesheet card shows a warning icon, it means the submission deadline is approaching.',
        'Use the keyboard shortcut ? to open the Help drawer from anywhere in the application.',
      ],
    },
    faq: [
      {
        question: 'What does the homepage show if I have no pending actions?',
        answer: 'If your timesheet is already submitted and there are no alerts, the homepage shows your submitted hours and a confirmation status. No action is required.',
      },
      {
        question: 'What does it mean if my timesheet shows a rejected status?',
        answer: 'A rejected status means your manager has reviewed your timesheet and sent it back for changes. An alert on the homepage will indicate which week is affected. Click through to view the rejection comment, make your corrections, and resubmit.',
      },
      {
        question: 'Why am I seeing an overdue alert on the homepage?',
        answer: 'An overdue alert appears when you have not submitted a timesheet by the expected deadline. Click the alert to open the relevant timesheet, complete your entries, and submit it for approval.',
      },
      {
        question: 'How do I deal with an Action Required item?',
        answer: 'Action Required items appear when something needs your attention — such as an overdue timesheet, a rejected submission, or an upcoming deadline. Each item shows a brief description of the issue. Click the item or its associated button to be taken directly to the relevant timesheet. From there you can make any necessary corrections and resubmit. Once the action has been resolved, the item will disappear from the Action Required section automatically.',
      },
    ],
    relatedPages: [
      { label: 'My Timesheet', path: '/timesheet' },
      { label: 'Approvals', path: '/approvals' },
      { label: 'Timesheet Reports', path: '/reports/timesheet' },
    ],
  },

  '/manager': {
    pageTitle: 'Manager View',
    description: 'An overview of your team\'s timesheet status, pending approvals, and submission activity.',
    icon: Users,
    functionality: {
      overview:
        'The Manager View is the homepage for users with approval rights. It gives you a consolidated picture of your team\'s timesheet activity — who has submitted, who is overdue, what is pending your approval, and how team hours are tracking against targets for the current period.',
      keyFeatures: [
        { label: 'Key Metrics', detail: 'Four summary cards at the top: Pending Approvals, Missing Timesheets, Total Hours, and Utilisation Rate — giving you an instant health check of the team.' },
        { label: 'Quick Actions', detail: 'Shortcut cards to jump straight to Review Approvals, Team Overview, and Utilisation Reports without navigating through menus.' },
        { label: 'Alerts & Notifications', detail: 'A live panel surfacing the most urgent issues — overdue approvals and team members below utilisation target — with dismiss and review actions.' },


      ],
      sectionsAndFields: [
        { name: 'Key Metrics', description: 'Four metric cards: Pending Approvals (links to Approvals screen), Missing Timesheets (links to Missing Timesheets report), Total Hours, and Utilisation Rate.' },
        { name: 'Quick Actions', description: 'Three clickable shortcut cards: Review Approvals, Team Overview, and Utilisation Reports — each navigates directly to the relevant screen.' },
        { name: 'Alerts & Notifications', description: 'Dismissible alert cards highlighting overdue approvals and under-utilised team members. Only visible when active alerts exist.' },
        { name: 'AI-Powered Insights — Predictive Analytics', description: 'AI-generated cards showing utilisation trend predictions, available resource capacity, and employees at risk of overallocation.' },
        { name: 'AI-Powered Insights — Capacity Planning', description: 'Progress bars for available capacity, allocated hours, and over-allocated employees. Pulls live data from the Utilisation API when connected.' },
        { name: 'Analytics & Trends — Utilisation Trend', description: 'A horizontal bar chart showing utilisation percentage for each of the last 8 weeks, with an average and trend indicator.' },
        { name: 'Analytics & Trends — Project Allocation', description: 'A progress-bar breakdown of the top four projects by team hours, showing hours logged and percentage of total.' },
        { name: 'Reports & Export', description: 'Three export buttons: Team Summary (PDF), Utilisation Data (CSV), and Project Hours (CSV).' },
        { name: 'Team Performance', description: 'Scrollable list of all team members with search, filter (All / Has Pending / Active Only), and sort (Name / Hours / Utilisation) controls. Supports checkbox selection for bulk approve.' },
        { name: 'Recent Team Activity', description: 'Scrollable activity feed filterable by All, Submitted, Approved, or Overdue. Each row shows the employee, action, description, and relative timestamp.' },
      ],
      useCases: [
        'Checking at a glance how many timesheets are pending approval before starting the day.',
        'Identifying team members with missing or overdue timesheet submissions.',
        'Bulk approving timesheets for multiple team members in a single action.',
        'Reviewing utilisation trends over the last 8 weeks to spot performance changes.',
        'Exporting a team summary or project hours report for a client billing run.',
        'Assessing team capacity to decide whether to take on additional project work.',
        'Jumping directly to a specific team member\'s timesheet from the Team Performance list.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Check the alerts section first — it surfaces anything that needs immediate attention across your team. From here you can jump straight to pending approvals or missing timesheets using the Quick Actions.',
      steps: [
        {
          title: 'How do I approve a team member\'s timesheet?',
          items: [
            { text: 'Locate the Pending Approvals section on the Manager View.' },
            { text: 'Click Show Me to navigate directly to the Approvals page.' },
            { text: 'Click the team member\'s name or the timesheet row to open the detail view.' },
            { text: 'Review the hours and project allocations.' },
            { text: 'Click Approve to confirm, or Reject to decline.', tip: 'Add a rejection reason in the comment field — this is sent directly to the team member so they know what to correct.' },
            { note: 'Approved timesheets are locked for editing by the employee. If a correction is needed after approval, you must unlock the timesheet from the Approvals screen.' },
          ],
        },
        {
          title: 'How do I see which team members are overdue?',
          items: [
            { text: 'Check the Missing Timesheets card on the Manager View — it will show the total of timesheets that have not submitted.' },
            { text: 'Click \'Show Me\' to navigate to the Missing Timesheet Report.' },
            { text: 'Click a team member\'s name to open their timesheet and see their current draft or empty entries.' },
          ],
        },
        {
          title: 'How do I view a specific team member\'s full timesheet?',
          items: [
            { text: 'Find the team member in the Team Member List or Click Pending Approvals to view all approvals.' },
            { text: 'Click their name or the arrow icon to navigate directly to their timesheet.' },
            { text: 'Review their entries in the full timesheet grid.', note: 'You are viewing the timesheet in read-only mode unless you explicitly unlock it for editing.' },
          ],
        },
        {
          title: 'How do I reject a timesheet and request changes?',
          items: [
            { text: 'Open the timesheet from the Pending Approvals section.' },
            { text: 'Click Reject.' },
            { text: 'Type your reason in the comment field — be specific about what needs to be corrected.' },
            { text: 'Click Confirm Rejection.', note: 'The team member receives a notification with your comment and the timesheet is returned to draft status for them to edit and resubmit.' },
          ],
        },
      ],
      proTips: [
        'Approve timesheets in bulk from the Approvals screen when you have a large team — it is faster than reviewing each one individually from the Manager View.',
        'The overdue list is sorted by days overdue, not alphabetically — the most urgent cases always appear at the top.',
        'If a team member\'s hours look unusual, click through to their full timesheet before approving — the summary view only shows totals, not the project breakdown.',
        'Switch back to your personal Employee View before logging your own time — entries made in Manager View may be attributed incorrectly.',
      ],
    },
    faq: [
      {
        question: 'Can I approve multiple timesheets at once?',
        answer: 'Bulk approval is available on the full Approvals screen. The Manager View shows individual approval actions — navigate to Approvals for bulk processing.',
        steps: [
          'Go to the Approvals screen from the left navigation.',
          'Select the checkboxes next to the timesheets you want to approve.',
          'Click Approve Selected.',
        ],
      },
      {
        question: 'Can I edit a team member\'s timesheet myself?',
        answer: 'Whether you can edit a team member\'s timesheet depends on your approval workflow settings. If editing is enabled, you can make changes directly before approving. If not, you can reject it with a comment so the employee corrects and resubmits. Check with your system administrator if you are unsure what is configured for your organisation.',
      },
      {
        question: 'Why is a team member missing from my Manager View?',
        answer: 'The Manager View only shows employees in the same department as you. If a team member is missing, their department assignment may not be configured correctly — contact your system administrator to update the department structure.',
      },
      {
        question: 'How do I see timesheets from previous weeks?',
        answer: 'The Manager View defaults to the current week. Use the Timesheet Reports screen (found in the left navigation, under reports) to view team submissions for any previous period.',
      },
      {
        question: 'What does "overdue" mean on the Manager View?',
        answer: 'A timesheet is marked overdue when a team member has not submitted it by the deadline configured for your organisation. The number of days overdue is shown next to their name.',
      },
      {
        question: 'Can I unlock a timesheet I have already approved?',
        answer: 'No. Once a timesheet has been approved it is locked and can only be unlocked by a system administrator. If a correction is needed after approval, contact your administrator to unlock it and return it to the employee for editing.',
      },
    ],
    relatedPages: [
      { label: 'Approvals', path: '/approvals' },
      { label: 'Missing Timesheets', path: '/reports/missing' },
      { label: 'Timesheet Reports', path: '/reports/timesheet' },
      { label: 'Team Members', path: '/team' },
    ],
  },

  '/timesheet': {
    pageTitle: 'Timesheet',
    description: 'Enter, edit, and submit your weekly time entries against projects and cost centres.',
    icon: Clock,
    functionality: {
      overview:
        'Your Timesheet is where you track the hours you work each week. Pick the projects you\'ve worked on, fill in your hours day by day, save as you go, and submit to your manager when you\'re ready.',
      keyFeatures: [
        { label: 'Save Draft', detail: 'Saves your progress without submitting, allowing you to return and complete it later.' },
        { label: 'Submit for Approval', detail: 'Sends your completed timesheet to your manager for review and sign-off.' },
        { label: 'Recall', detail: 'Retrieves a submitted timesheet so you can make corrections before it is approved.' },
        { label: 'Insights', detail: 'Scans your timesheet and surfaces detected issues — such as policy violations, missing hours, or unusual patterns — so you can review and act on them before submitting.' },
        { label: 'History', detail: 'Opens the audit history for the current timesheet, showing a full log of saves, submissions, approvals, and rejections.' },
      ],
      sectionsAndFields: [
        { name: 'Week Navigation Bar', description: 'Sits at the top of the page. Contains left/right arrows to move between weeks, the current week date range, and the status badge for that week.' },
        { name: 'Status Badge', description: 'Shows the current state of the timesheet: Not Started, Draft, Submitted, Approved, or Rejected.' },
        { name: 'Rejection Banner', description: 'A prominent red alert that appears when the timesheet has been rejected. Displays the rejecting manager\'s name, date, rejection reason, affected entries, and a step-by-step guide on what to do next.' },
        { name: 'Timesheet Grid', description: 'A scrollable table with one row per project and a column for each working day. Click any cell to enter or edit hours.' },
        { name: 'Project Column', description: 'Shows the name of the project the row is associated with. Required — a row cannot be submitted without a project selected.' },
        { name: 'Cost Centre Column', description: 'Visible when Cost Centre tracking is enabled. Shows the cost centre assigned to the row.' },
        { name: 'Role Column', description: 'Visible when Role tracking is enabled. Shows the role assigned to the row.' },
        { name: 'Work Type Column', description: 'Visible when Work Type Tracking is enabled. A dropdown to classify the row as Standard or an overtime type (e.g. Time & a Half). Read-only badge when approved.' },
        { name: 'Day Columns', description: 'One column per working day. Click a cell and type the number of hours worked on that project for that day. Decimals supported (e.g. 1.5).' },
        { name: 'Total Column', description: 'Automatically sums the hours entered across all day cells for that project row.' },
        { name: 'Daily Totals Row', description: 'A sticky footer row at the bottom of the grid showing the total hours logged across all projects for each individual day.' },
        { name: 'Add Row Button', description: 'Adds a new project row to the grid. Opens a project search so you can select the project to log time against.' },
        { name: 'Copy Last Week Button', description: 'Populates the current week with the same project rows used last week, saving you from manually re-adding your usual projects.' },
        { name: 'Smart Auto-Fill Button', description: 'Opens a dialog showing suggested project rows and hours based on your recent history. Tick the suggestions you want and click Apply.' },
        { name: 'Save Draft Button', description: 'Saves your current entries without submitting. Lets you return and complete the timesheet later.' },
        { name: 'Submit for Approval Button', description: 'Submits the timesheet to your manager for review. Only active when entries exist and all required fields are complete.' },
        { name: 'Recall Button', description: 'Appears when the timesheet is in Submitted status. Returns it to Draft so you can make corrections before approval.' },
        { name: 'Insights Button', description: 'Appears in the footer when issues are detected. Opens a panel showing policy violations, missing hours, or anomalies — with auto-fix options where available.' },
        { name: 'History Button', description: 'Located in the page header. Opens the audit history for the current timesheet showing all saves, submissions, approvals, and rejections.' },
      ],
      useCases: [
        'Recording daily hours across multiple projects at the end of each day or week.',
        'Correcting an error in a submitted timesheet by recalling it before the manager approves it.',
        'Reviewing what you logged in a previous week.',
        'Checking how many hours remain before you hit your weekly limit.',
        'Using Smart Auto-Fill to pre-populate a week based on your usual projects, then adjusting individual hours before submitting.',
        'Reviewing the Insights panel before submitting to catch any policy violations or missing hours early.',
        'Classifying overtime hours with a Work Type (e.g. Time & a Half) when you have worked beyond your standard contracted hours.',
        'Reviewing scheduled rows at the start of the week — adjusting hours where needed, or dismissing a booking that no longer applies.',
        'Checking absence rows to confirm that leave imported from the HR system matches what you expected — and contacting your HR team if there is a discrepancy.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Start by clicking Add Row to add a project to the grid, then enter your hours for each day. Save as Draft as you go, and click Submit for Approval when your week is complete.',
      steps: [
        {
          title: 'How do I add a project to my timesheet?',
          items: [
            { text: 'Click the Add Row button below the timesheet grid.' },
            { text: 'Type the first few characters of the project name in the search field.' },
            { text: 'Select the correct project from the dropdown results. If you cannot find the right project try clicking "Show more" to filter against project details', note: 'If a project does not appear in the search, it may be inactive or you may not have permission to log against it. Contact your administrator.' },
            { text: 'The project row appears in the grid, ready for hour entry.' },
          ],
        },
        {
          title: 'How do I enter hours?',
          items: [
            { text: 'Click the cell in the grid that corresponds to the project row and the correct day column.' },
            { text: 'Type the number of hours (decimals are supported, e.g. 1.5 for 90 minutes).', tip: 'Press Tab to move to the next day cell without using the mouse.' },
            { text: 'Click outside the cell or press Enter to confirm the entry.' },
          ],
        },
        {
          title: 'How do I submit my timesheet?',
          items: [
            { text: 'Ensure all your hours for the week are entered.' },
            { text: 'Click the Submit for Approval button.', note: 'Once submitted, you cannot edit the timesheet unless you recall it or your manager rejects it.' },
            { text: 'The status badge changes to Submitted and your manager receives a notification.' },
          ],
        },
        {
          title: 'How do I recall a submitted timesheet?',
          items: [
            { text: 'Navigate to the week containing the submitted timesheet.' },
            { text: 'Click the Recall button.', note: 'Recall is only available while the timesheet is in Submitted status. Once a manager has approved it, recall is no longer possible.' },
            { text: 'The timesheet returns to Draft status and you can edit it.' },
            { text: 'Re-submit when your changes are complete.' },
          ],
        },
        {
          title: 'How do I use Smart Auto-Fill?',
          items: [
            { text: 'Click the Smart Auto-Fill button in the timesheet toolbar (indicated by a sparkle icon).', note: 'This button only appears if Smart Auto-Fill is enabled for your organisation and role.' },
            { text: 'A dialog opens showing suggested project rows and hours based on your recent timesheet history.' },
            { text: 'Tick the suggestions you want to apply — you can select all or just individual rows.' },
            { text: 'Click Apply. The selected rows and hours are added to the grid for you to review and adjust before saving or submitting.', tip: 'Smart Auto-Fill adds suggestions but never overwrites hours you have already entered.' },
          ],
        },
        {
          title: 'How do I use Insights?',
          items: [
            { text: 'Click the Insights button in the timesheet toolbar (indicated by a sparkle icon).', note: 'This button only appears if Insights is enabled for your organisation and role.' },
            { text: 'The Insights panel opens on the right, showing any detected issues grouped by category — for example, Policy Violations, Missing Hours, or Data Quality.' },
            { text: 'Each issue shows a severity level (High, Medium, or Low) and a description of what was detected.' },
            { text: 'Where available, click Auto-fix to let the system resolve the issue automatically, or dismiss issues you have already reviewed.', tip: 'Use the Filter button to focus on high-severity issues first.' },
          ],
        },
        {
          title: 'How do I assign a Work Type to a row?',
          items: [
            { text: 'Ensure Work Type Tracking is enabled — if the Work Type column is not visible, the feature is not turned on for your organisation. Contact your administrator.', note: 'Work Types are configured under Settings → Overtime.' },
            { text: 'Locate the Work Type column on the right side of the timesheet grid.' },
            { text: 'Click the dropdown in the row you want to classify.' },
            { text: 'Select the appropriate type — for example, Standard, Time & a Half, or Double Time.', tip: 'Absence rows always show a dash in the Work Type column. Work types cannot be assigned to absence entries.' },
          ],
        },
        {
          title: 'What do I do with a scheduled row?',
          items: [
            { text: "Rows pre-populated from the scheduler appear with a blue 'From Schedule' accent. Review the hours and edit them directly in the grid if they differ from what you actually worked." },
            { text: 'If the booking no longer applies, open the row action menu (three dots located at the end of each row) and select Dismiss Booking. The row stays on the timesheet but the scheduled link is removed.' },
            { text: 'If you dismissed a booking by mistake, open the row action menu and select Restore Booking to reinstate the scheduled link.', tip: 'Insights will flag if your logged hours differ significantly from the scheduled hours — look for Scheduled Booking Variance issues in the Insights panel.' },
          ],
        },
        {
          title: 'Why can\'t I edit an absence row?',
          items: [
            { text: 'Rows with a purple "Absence" accent (e.g. Annual Leave, Sick Leave) are imported from your HR or absence management system. They are read-only in the timesheet.' },
            { text: 'If the absence entry is incorrect — for example, the wrong dates or leave type — contact your HR team or administrator to correct it in the absence system.' },
            { text: 'Once corrected at source, the row in the timesheet will update automatically.', note: 'You cannot delete or modify absence rows yourself. Any changes must be made by HR or a system administrator.' },
          ],
        },
        {
          title: 'How do I view a previous week\'s timesheet?',
          items: [
            { text: 'Click the left arrow next to the week selector at the top of the page.' },
            { text: 'Continue clicking until you reach the week you want to view.' },

          ],
        },
      ],
      proTips: [
        'Use Tab to move between day cells quickly — it is faster than clicking each cell.',
        'Save as Draft regularly if you are entering hours across multiple sessions so you do not lose progress.',
        'If your timesheet is rejected, check the rejection comment from your manager before making changes — it will explain what needs correcting.',
        'You can enter 0 in a cell to explicitly mark a day as non-working without deleting the project row.',
        'Check Insights before you submit — catching a policy violation early is much easier than recalling an already-submitted timesheet.',
      ],
    },
    faq: [
      {
        question: 'Can I enter time in half-hour increments?',
        answer: 'Yes. Enter decimals in any hour cell — for example, 0.5 for 30 minutes or 1.5 for 90 minutes.',
      },
      {
        question: 'What does the Rejected status mean?',
        answer: 'Your manager has reviewed your timesheet and requested changes. Open the timesheet, read the rejection comment, make the necessary corrections, and re-submit.',
      },
      {
        question: 'How do I delete a project row I added by mistake?',
        answer: 'Click the row options menu (three dots) on the right side of the project row and select Remove Row.',
        steps: ['Click the three-dot menu on the project row.', 'Select Delete, to remove the Row.', 'Confirm the deletion if prompted.'],
      },
      {
        question: 'Why is the Submit button greyed out?',
        answer: 'The Submit button is disabled when the timesheet is already in Submitted or Approved status, or when no hours have been entered. Check the status badge and ensure at least one hour is recorded before submitting.',
      },
      {
        question: 'Can I copy last week\'s timesheet to save time?',
        answer: 'Yes. Use the Copy Previous Week option in the timesheet actions menu to populate this week\'s grid with the same project rows as last week. You still need to enter this week\'s hours.',
      },
      {
        question: 'What happens if I exceed my contracted weekly hours?',
        answer: 'Depending on your organisational rules, the timesheet will display a warning indicator but could prevent your submission over a certain percent. Check with your administrator to learn about your organisation rules. If you can submit exceeded hours, your manager will see the overage during their review.',
      },
      {
        question: 'What are the different Work Types?',
        answer: 'Not all organisations will use Work Types, so you may not see it on your timesheet, check with your administrator if you are unsure.\n\nWork Types are configured by your administrator under Settings → Overtime. Typical types include Standard (normal hours), Time & a Half (1.5× rate), and Double Time (2× rate), but your organisation may have different types set up. If you are unsure which type to select, check with your manager.',
      },
      {
        question: 'Can I delete a scheduled row?',
        answer: 'You can only delete a scheduled row after you have dismissed the booking first. Open the row action menu, select View Details, then Dismiss Booking. Then open the menu again and select Delete Row.',
      },
      {
        question: 'Why are there rows on my timesheet I did not add?',
        answer: 'There are two likely reasons: the row has a blue Scheduled mark, meaning it was pre-populated from the project scheduler; or it has an Absence mark, meaning leave was imported from your HR system. Both are expected and can be reviewed using the information in the row\'s action menu.',
      },
      {
        question: 'Why can\'t I see the Smart Auto-Fill button?',
        answer: 'Smart Auto-Fill is controlled by two settings — it must be enabled at the organisation level and also permitted for your role. If the button is not visible, contact your administrator to check whether the feature is turned on for you.',
      },
      {
        question: 'Why can\'t I see the Insights button?',
        answer: 'Like Smart Auto-Fill, Insights must be enabled at both the organisation and role level. Contact your administrator if you expect to have access but the button is not visible.',
      },
    ],
    relatedPages: [
      { label: 'Homepage', path: '/' },
      { label: 'Approvals', path: '/approvals' },
      { label: 'Timesheet Reports', path: '/reports/timesheet' },
    ],
  },

  '/approvals': {
    pageTitle: 'Approvals',
    description: 'Review, approve, or reject timesheet submissions from your department members.',
    icon: CheckCircle2,
    functionality: {
      overview:
        'The Approvals page is where managers and administrators review timesheets submitted by their department members. You can approve or reject individual timesheets, add comments, and view the complete submission history for each employee.',
      keyFeatures: [
        { label: 'Filters', detail: 'Filter the list by Department, issue status (All Items, With Issues, No Issues), or toggle Overdue only — active filters appear as removable chips above the list.' },
        { label: 'Bulk Actions', detail: 'Select multiple timesheets using the checkboxes, then use Approve Selected or Reject Selected to act on them all at once.' },
      ],
      sectionsAndFields: [
        { name: 'KPI Summary Bar', description: 'Five metric cards: Pending Approvals, Overdue, With Anomalies, High Severity, and Hours Pending. Overdue and With Anomalies cards are clickable to filter the list.' },
        { name: 'Department Filter', description: 'A dropdown to filter the approvals list by department.' },
        { name: 'Issues Filter', description: 'A dropdown to filter by issue status — All Items, With Issues, or No Issues.' },
        { name: 'Overdue Button', description: 'A toggle button to show only overdue timesheets. Displays a count badge when overdue items exist.' },
        { name: 'Active Filter Chips', description: 'Removable chips shown above the list when filters are active — click the X on any chip to clear that filter.' },
        { name: 'Column Configurator', description: 'To re-order or hide columns to your preference.' },
        { name: 'Pending Approvals List', description: 'A table of all submitted timesheets awaiting action, showing employee name, week, total hours, waiting time, and issue indicators.' },
        { name: 'Approve Button', description: 'Approves the selected timesheet and notifies the employee.' },
        { name: 'Reject Button', description: 'Rejects the timesheet and requires a comment to be sent to the employee.' },
        { name: 'Approve Selected', description: 'Bulk action button — visible when one or more rows are checked. Approves all selected timesheets at once.' },
        { name: 'Reject Selected', description: 'Bulk action button — visible when one or more rows are checked. Rejects all selected timesheets at once.' },
      ],
      useCases: [
        'Reviewing and approving a team member\'s timesheet at the end of the week.',
        'Opening the detail view to check daily hours, project rows, and notes before deciding to approve or reject.',
        'Using the Insights panel to review detected anomalies and policy violations before approving a flagged timesheet.',
        'Editing a timesheet directly in the detail view to correct a minor error rather than rejecting and sending it back.',
        'Rejecting a timesheet and explaining to the employee what needs correcting.',
        'Approving multiple timesheets in bulk when you have already reviewed them.',
        'Acting on timesheets that have been delegated to you while covering for another manager.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Each row is a submitted timesheet waiting for your action. Click any row to open the full detail view before deciding to approve or reject. Use the filters to prioritise overdue or flagged submissions first.',
      steps: [
        {
          title: 'How do I view the full details of a timesheet?',
          items: [
            { text: 'Click anywhere on a timesheet row in the Pending Approvals list.' },
            { text: 'The detail view opens, showing each project row with daily hours, work types, descriptions, and any scheduled or absence entries.' },
            { text: 'Click on insights to show the timesheet insights panel — this shows any detected anomalies or policy violations.' },
            { text: 'Use the Submission History button to see the full audit trail for that timesheet.', tip: 'Check the history if a timesheet has been submitted more than once — it shows you every prior action and who took it.' },
          ],
        },
        {
          title: 'How do I approve a timesheet?',
          items: [
            { text: 'Find the timesheet in the Pending Approvals list.' },
            { text: 'Click the timesheet row to open the detail view and review the hours.' },
            { text: 'Click Approve.' },
            { text: 'The employee receives an approval notification automatically.', tip: 'Or approve it directly from the grid by clicking the approve button to the right if you are happy the submission is correct.' },
          ],
        },
        {
          title: 'How do I reject a timesheet?',
          items: [
            { text: 'Find the timesheet in the Pending Approvals list.' },
            { text: 'Click the timesheet row to open the detail view. Or click reject directly from the timesheet grid.' },
            { text: 'Click Reject.' },
            { text: 'Type a clear explanation in the comment field.', note: 'A comment is required when rejecting. The employee will see this comment and needs it to understand what to correct.' },
            { text: 'Click Confirm Rejection. The timesheet returns to the employee as a Draft.' },
          ],
        },
        {
          title: 'How do I edit a timesheet before approving?',
          items: [
            { text: 'Open the timesheet detail view by clicking the row.' },
            { text: 'Click the Edit button in the top right of the detail view.', note: 'The Edit button is only visible if your role has editing in approval enabled. If you do not see it, contact your administrator.' },
            { text: 'Make your changes to hours, project, description, or daily notes directly in the grid.' },
            { text: 'Click Save Changes to commit the edits, then click Approve to approve the updated timesheet.' },
          ],
        },
        {
          title: 'How do I use the Insights panel when reviewing a timesheet?',
          items: [
            { text: 'Open the timesheet detail view by clicking the row.' },
            { text: 'To open the Insights panel click the Insights button on the right side of the detail view — it will open a side panel.' },
            { text: 'Review any detected anomalies (e.g. excessive hours, unusual patterns) and policy violations listed, each with a severity level (High, Medium, Low).' },
            { text: 'Use the information to decide whether to approve, edit, or reject the timesheet.', tip: 'A High severity issue is worth investigating before approving. Low severity issues are informational — they may not require action.' },
            { text: 'The Insights panel is read-only — it surfaces information to support your decision but does not block approval.' },
          ],
        },
        {
          title: 'How do I approve multiple timesheets at once?',
          items: [
            { text: 'Select the checkboxes next to the timesheets you want to approve.' },
            { text: 'Click the Approve Selected button that appears in the action bar.', tip: 'Use bulk approve only when you have already reviewed the timesheets. Individual approval gives you a chance to check the detail first.' },
            { text: 'Confirm the bulk action. All selected timesheets are marked as Approved.' },
          ],
        },
        {
          title: 'How do I handle timesheets delegated to me?',
          items: [
            { text: 'When another manager has delegated their approval authority to you, a separate section appears in the Approvals list labelled with their name.' },
            { text: 'A banner at the top of the page will also notify you that you have active delegations.' },
            { text: 'Review and approve or reject those timesheets exactly as you would your own queue.', note: 'Actions you take on delegated timesheets are recorded in the audit trail and attributed to you as the acting approver.' },
            { text: 'The delegated section disappears automatically once the delegation period ends or is revoked.' },
          ],
        },
      ],
      proTips: [
        'Use the Items filter in the filter bar to quickly isolate timesheets flagged with issues and review those before moving on to clean submissions.',
        'If a timesheet only needs a small correction, use Edit in Approval rather than rejecting — it is faster for both you and the employee.',
        'When rejecting, be specific in your comment — vague feedback like "please fix" causes unnecessary back-and-forth.',
        'When covering for another manager via delegation, check the delegated section every day — it is separate from your own queue and easy to miss.',
      ],
    },
    faq: [
      {
        question: 'Can I approve a timesheet I have already rejected?',
        answer: 'Yes. Once the employee has corrected and re-submitted the timesheet, it will reappear in your Pending Approvals list and you can approve it.',
      },
      {
        question: 'What are the Insights anomalies and violations telling me?',
        answer: 'Anomalies are patterns detected by the system that look unusual compared to the employee\'s history — for example, a significant spike in hours or entries that look inconsistent. Policy violations are breaches of rules configured by your administrator, such as exceeding a maximum daily hours limit. Both are graded High, Medium, or Low. They are informational — they support your decision but do not automatically block approval.',
      },
      {
        question: 'Can I edit a timesheet before approving it?',
        answer: 'It depends on your approval workflow settings. If editing is enabled for your role, an Edit button will appear in the timesheet detail view. If you do not see it, contact your administrator — they can enable this permission for your role.',
      },
      {
        question: 'Why can I see timesheets from another manager\'s team?',
        answer: 'That manager has delegated their approval authority to you for a period. Their team\'s timesheets appear in a separate section of your queue labelled with their name. The delegation ends automatically on the configured end date, or the manager can revoke it earlier.',
      },
      {
        question: 'What happens if I do not approve a timesheet before payroll runs?',
        answer: 'Unapproved timesheets are excluded from payroll processing. Check your organisation\'s payroll deadline — timesheets must be approved before that date to be included.',
      },
      {
        question: 'Can I delegate my approval authority to someone else?',
        answer: 'Yes. Navigate to Approvals → Delegations and create a new delegation for the dates you will be unavailable.',
        steps: ['Go to Approvals → Delegations.', 'Click Set Up Delegation.', 'Select the delegate and set the date range.', 'Click Save.', 'You can also reassign any pending timesheets before completing.'],
      },
      {
        question: 'What does the total hours figure include?',
        answer: 'The total hours shown in the approvals list is the sum of all hours logged across every project row in that week\'s timesheet.',
      },
    ],
    relatedPages: [
      { label: 'Homepage', path: '/' },
      { label: 'My Timesheet', path: '/timesheet' },
      { label: 'Timesheet Reports', path: '/reports/timesheet' },
      { label: 'Delegations', path: '/approvals/delegations' },
    ],
  },

  '/reports/timesheet': {
    pageTitle: 'Timesheet Reports',
    description: 'View, filter, and export timesheet data. Managers and admins see their full team; other users see their own timesheets only.',
    icon: FileText,
    functionality: {
      overview:
        'The Timesheet Reports page lets you view, filter, and export timesheet submissions. If you have manager or admin permissions you can see submissions across your team or the full organisation. If not, the report shows your own timesheets only. In both cases you can filter by date range, drill down into individual entries, and export results.',
      keyFeatures: [
        { label: 'Quick Filters', detail: 'Filter buttons for date range and status — apply one or more to narrow the report instantly.' },
        { label: 'Advanced Filters', detail: 'Opens a filter drawer with additional options including department, cost centre, work type, and grouping.' },
        { label: 'Drill Down', detail: 'Click any row in the report grid to open the full timesheet detail view for that employee and week.' },
      ],
      sectionsAndFields: [
        { name: 'Search Bar', description: 'Free-text search to filter the grid by employee name, project, or any visible field.' },
        { name: 'Quick Filters', description: 'Date range, status, and filter controls in the toolbar.' },
        { name: 'Advanced Filters', description: 'A slide-out drawer with additional filter options including department, cost centre, work type, and grouping.' },
        { name: 'Active Filter Chips', description: 'Chips above the grid showing active filters — click X to remove individual filters or clear all at once.' },
        { name: 'Column Configurator', description: 'Opens a panel to show or hide columns and save layout presets.' },
        { name: 'Export Button', description: 'Downloads the current filtered report as an Excel (.xlsx) file.' },
        { name: 'Report Grid', description: 'A table of timesheet entries matching the current filters, showing employee, week, project, hours, status, and more depending on column configuration.' },
        { name: 'Row Drilldown', description: 'Clicking a row opens the full timesheet detail view for that employee and week.' },
      ],
      useCases: [
        'Producing a list of all approved timesheets for a selected date range.',
        'Identifying employees who have not submitted timesheets for a given week.',
        'Checking total hours logged against a specific project across multiple employees.',
        'Running a full timesheet report across all projects and team members for a selected period.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Start by selecting a date range using the All Time dropdown — this controls all data shown in the grid. Narrow results further using status or advanced filters, then click any row to drill into the full timesheet detail.',
      steps: [
        {
          title: 'How do I filter the report by date range?',
          items: [
            { text: 'Click the "All Time" control in the filter bar.' },
            { text: 'Select a quick range (e.g. This Month, Last Month) or set a custom start and end date.' },
            { text: 'Click Apply. The report reloads with results for that period.' },
            { text: 'Or for more specific filtering use the Advanced Filters section.' },
          ],
        },
        {
          title: 'How do I export the report to Excel?',
          items: [
            { text: 'Apply any filters you need to narrow the data.' },
            { text: 'Click the Export button in the top right of the report.' },
            { text: 'Select Excel (.xlsx) or CSV from the format options.', tip: 'Use CSV if you are importing into a payroll system — Excel format preserves formatting but may not be compatible with all import tools.' },
            { text: 'The file downloads automatically to your browser\'s default download location.' },
          ],
        },
        {
          title: 'How do I view an individual timesheet from the report?',
          items: [
            { text: 'Find the row for the employee and week you want to review.' },
            { text: 'Click anywhere on the row to open the timesheet detail view.' },
            { text: 'Review the daily breakdown and project entries.' },
          ],
        },
      ],
      proTips: [
        'Combine employee and status filters to quickly find all rejected timesheets for a specific person.',
        'Run this report at the end of each pay period before approving to catch any missing or draft timesheets.',
      ],
    },
    faq: [
      {
        question: 'Why can I only see my own timesheets?',
        answer: 'The report is scoped to what your account has permission to view. Users without approval rights see their own timesheets only. Users with team access can see submissions from everyone in their department, and admins can see the full organisation. If you need broader access, contact your administrator.',
      },
      {
        question: 'Can I export the report in PDF format?',
        answer: 'PDF export is not currently available. Use Excel or CSV export and print from there if a hard copy is needed.',
      },
      {
        question: 'How do I find employees who have not submitted their timesheet?',
        answer: 'Set the Status filter to Not Started or Draft and select the relevant date range. The report will show employees who have not yet submitted.',
      },
    ],
    relatedPages: [
      { label: 'Approvals', path: '/approvals' },
      { label: 'Missing Timesheets', path: '/reports/missing-timesheets' },
      { label: 'Utilisation Report', path: '/reports/utilisation' },
    ],
  },

  '/reports/missing-timesheets': {
    pageTitle: 'Missing Timesheets',
    description: 'Identify employees who have not submitted a timesheet for a given period.',
    icon: AlertTriangle,
    functionality: {
      overview:
        'The Missing Timesheets report lists employees who have not submitted a timesheet for one or more weeks in the selected date range. You can send Access Evo Feed notifications to remind individuals or the entire outstanding list in a single action.',
      keyFeatures: [
        { label: 'Send Reminders', detail: 'Send an Access Evo Feed notification to all employees in the list, or tick specific rows to target only those individuals.' },
        { label: 'Column Configurator', detail: 'Show or hide columns to focus on the data most relevant to your review.' },
        { label: 'Export', detail: 'Downloads the current list as an Excel file for offline follow-up.' },
      ],
      sectionsAndFields: [
        { name: 'Total Missing', description: 'KPI card showing the total number of missing or incomplete timesheet submissions. Changes colour based on severity.' },
        { name: 'Compliance Rate', description: 'KPI card showing the percentage of expected timesheets that have been submitted.' },
        { name: 'Avg Days Overdue', description: 'KPI card showing the average number of days overdue across all missing submissions.' },
        { name: 'Repeat Offenders', description: 'KPI card showing the number of employees with critical overdue timesheets (11+ days).' },
        { name: 'Missing Timesheets Table', description: 'A table of all employees with missing or incomplete submissions, showing employee name, department, manager, week ending, status, expected vs submitted hours, days overdue, and last reminded.' },
        { name: 'Send Reminders Button', description: 'Sends an Access Evo Feed notification. With no rows selected it sends to all; with rows selected it sends only to those individuals.' },
        { name: 'Last Reminded Column', description: 'Shows when a reminder was last sent to each employee — useful for avoiding duplicate notifications.' },
        { name: 'Column Configurator', description: 'Opens a panel to show or hide columns in the table.' },
        { name: 'Export Button', description: 'Downloads the current table as an Excel (.xlsx) file.' },
      ],
      useCases: [
        'Identifying which employees have not submitted timesheets for a given period.',
        'Sending a bulk Access Evo Feed notification to all outstanding submitters in one click.',
        'Targeting a reminder at a specific employee without notifying the rest of the list.',
        'Producing a record of non-submission for compliance or audit purposes.',
      ],
    },
    userGuide: {
      gettingStarted:
        'The list shows all employees with missing or incomplete timesheets. Once you have identified who needs chasing, select them and click Send Reminders to notify them directly.',
      steps: [
        {
          title: 'How do I send a reminder to all employees with missing timesheets?',
          titleAlign: 'left',
          items: [
            { text: 'Set the period filter to the relevant week or date range.' },
            { text: 'Leave all checkboxes unticked.' },
            { text: 'Click Send Reminders (All).', note: 'An Access Evo Feed notification is sent to every employee currently shown in the list.' },
          ],
        },
        {
          title: 'How do I send a reminder to specific employees only?',
          items: [
            { text: 'Set the period filter to the relevant week or date range.' },
            { text: 'Tick the checkboxes next to the employees you want to remind.' },
            { text: 'Click Send Reminders. The button shows the number of selected employees, e.g. Send Reminders (3).', note: 'Only the selected employees will receive an Access Evo Feed notification.' },
          ],
        },
      ],
      proTips: [
        'Check the Last Reminded column before sending — avoid sending multiple reminders to the same employee in a short period.',
        'Use Send Reminders (All) for a quick sweep at the start of the week, then follow up with targeted reminders for persistent non-submitters.',
      ],
    },
    faq: [
      {
        question: 'What does "missing" mean — does it include Draft timesheets?',
        answer: 'A timesheet is considered missing if it is in Not Started or Draft status. Submitted and Approved timesheets are not included in this report.',
      },
      {
        question: 'How are reminders delivered to employees?',
        answer: 'Reminders are sent as Access Evo Feed notifications. Employees will see the notification in their Access Evo Feed — no email is sent.',
      },
      {
        question: 'Can I send a reminder to a single employee rather than the whole list?',
        answer: 'Yes. Tick only that employee\'s checkbox before clicking Send Reminders. The button will show the count, e.g. Send Reminders (1), confirming only that individual will be notified.',
      },
    ],
    relatedPages: [
      { label: 'Timesheet Reports', path: '/reports/timesheet' },
      { label: 'Approvals', path: '/approvals' },
      { label: 'Team Members', path: '/team/members' },
    ],
  },

  '/reports/utilisation': {
    pageTitle: 'Utilisation Report',
    description: 'Measure how effectively your team\'s available hours are being used across billable and non-billable work.',
    icon: TrendingUp,
    functionality: {
      overview:
        'The Utilisation Report calculates the percentage of each employee\'s available working hours that have been logged and approved. It helps managers identify over- or under-utilisation and make resourcing decisions.',
      keyFeatures: [
        { label: 'Date Range Filter', detail: 'A preset selector (This Week, Last Week, Last 30 Days, Last 90 Days, This Month, Last Month) that controls the period for all calculations on the page.' },
        { label: 'Department Filter', detail: 'A multi-select dropdown to narrow the report to one or more departments.' },
        { label: 'Export', detail: 'Downloads the current report data as a CSV file.' },
      ],
      sectionsAndFields: [
        { name: 'Date Range Selector', description: 'A preset dropdown — This Week, Last Week, Last 30 Days, Last 90 Days, This Month, or Last Month. The selected date range is displayed below the control.' },
        { name: 'Department Filter', description: 'Multi-select dropdown to filter all report sections by one or more departments.' },
        { name: 'KPI Cards', description: 'Summary metric cards at the top of the page showing headline utilisation figures for the selected period.' },
        { name: 'Performance Trends', description: 'A trend chart showing utilisation over time within the selected date range.' },
        { name: 'Utilisation Overview', description: 'A chart showing daily utilisation breakdown across the selected period.' },
        { name: 'Weekly Breakdown', description: 'Utilisation figures broken down week by week.' },
        { name: 'Project Allocation', description: 'A breakdown of hours allocated to each project. Clickable rows open a project detail modal.' },
        { name: 'Department Summary', description: 'Aggregated utilisation figures grouped by department.' },
        { name: 'Employee Performance', description: 'Per-employee utilisation figures. Clicking an employee opens a detail modal with their full breakdown.' },
        { name: 'Export Button', description: 'Downloads the full report as a CSV file.' },
      ],
      useCases: [
        'Reviewing team utilisation at the end of a month to identify capacity issues.',
        'Checking whether a specific employee is consistently over- or under-allocated.',
        'Producing a utilisation report for client billing reconciliation.',
        'Identifying projects or employees with high non-billable hour ratios.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Start by selecting a date range and department to focus the report. The KPI cards at the top give you an instant health check — scroll down to explore trends, project allocation, and individual employee breakdowns.',
      steps: [
        {
          title: 'How do I drill down into an individual employee\'s utilisation?',
          items: [
            { text: 'Click the employee\'s row in the team summary view.' },
            { text: 'The individual view opens, showing a week-by-week breakdown of their utilisation for the selected period.' },
          ],
        },
      ],
      proTips: [
        'A utilisation rate above 100% indicates logged hours exceed contracted hours — review for timesheet errors or overtime.',
      ],
    },
    faq: [
      {
        question: 'How is the utilisation percentage calculated?',
        answer: 'Utilisation % = (Approved Hours ÷ Available Hours) × 100. Available hours are derived from the employee\'s contracted working pattern for the selected period.',
      },
      {
        question: 'Why does an employee show 0% utilisation when I know they submitted a timesheet?',
        answer: 'The utilisation report uses approved timesheets only. If the timesheet is still in Submitted status and not yet approved, it will not appear in the calculation.',
      },
    ],
    relatedPages: [
      { label: 'Timesheet Reports', path: '/reports/timesheet' },
      { label: 'Missing Timesheets', path: '/reports/missing-timesheets' },
      { label: 'Team Members', path: '/team/members' },
    ],
  },

  '/team/members': {
    pageTitle: 'Team Members',
    description: 'View the employees in your team, including their roles, working patterns, and contact details. Employees are added via Access Evo.',
    icon: Users,
    functionality: {
      overview:
        'The Team Members page lists all employees you have visibility of, based on your role. You can view employee details, working patterns, and role assignments. Employees are created and managed in Access Evo — new users will appear here automatically once they have been set up there.',
      keyFeatures: [
        { label: 'Search', detail: 'Finds employees by name, email, role, or department as you type.' },
        { label: 'Department Filter', detail: 'Narrows the list to employees in a specific department.' },
        { label: 'Role Filter', detail: 'Narrows the list to employees with a specific role.' },
        { label: 'View Toggle', detail: 'Switch between a Grid view (photo cards) and a List view (compact rows).' },
      ],
      sectionsAndFields: [
        { name: 'Total Employees Card', description: 'Shows the total headcount and number of departments. Clickable — resets all status filters.' },
        { name: 'Active Card', description: 'Shows the count and percentage of active employees. Clickable — filters the list to active employees only.' },
        { name: 'Inactive Card', description: 'Shows the count of inactive employees. Clickable — filters the list to inactive employees only.' },
        { name: 'Largest Department Card', description: 'Displays the department with the most team members.' },
        { name: 'Search Bar', description: 'Filters the employee list by name, email, role, or department as you type.' },
        { name: 'Department Filter', description: 'A dropdown to filter by department.' },
        { name: 'Role Filter', description: 'A dropdown to filter by job role.' },
        { name: 'Sort By', description: 'Sorts the list by Name, Department, Role, or Email.' },
        { name: 'View Toggle', description: 'Switches between Grid (photo cards) and List (compact rows) layouts.' },
        { name: 'Employee Card / Row', description: 'Shows the employee photo, name, role, department, email, location, grade badge, and status indicator. Click to open full details.' },
        { name: 'View Details Button', description: 'Opens the employee detail dialog showing working pattern, contact info, grade, manager, and role.' },
      ],
      useCases: [
        'Looking up an employee\'s role or working pattern before approving their timesheet.',
        'Checking which employees are currently active in the system.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Use the search bar to find a specific team member, or filter by department or role to browse a group. Click any card or row to open the full employee detail view.',
      steps: [
        {
          title: 'How do I find a specific employee?',
          items: [
            { text: 'Type the employee\'s name in the search bar at the top of the list.' },
            { text: 'The list filters in real time as you type.' },
            { tip: 'You can also filter by role or department using the filter dropdowns next to the search bar.' },
          ],
        },
        {
          title: 'How do I view an employee\'s working pattern?',
          items: [
            { text: 'Click the employee\'s row to open their profile.' },
            { text: 'Select the Working Pattern tab in the employee detail view.' },
            { text: 'The contracted days and hours per day are listed for each day of the week.' },
          ],
        },
      ],
      proTips: [
        'Working patterns must be set correctly for utilisation reports to calculate accurately — an employee with no working pattern set will show 0% available hours.',
        'Use the Status filter to show only inactive employees if you are auditing who still has system access.',
      ],
    },
    faq: [
      {
        question: 'Can I add a new employee from this page?',
        answer: 'No. Employees are created and managed in Access Evo, not within the timesheet application. Once a user has been set up in Access Evo, they must be granted access to the Time module through Manage Roles before they will appear here.',
      },
      {
        question: 'Why can I not see all employees in the organisation?',
        answer: 'Your view is scoped to the employees you manage or have permission to see. Administrators see all employees. If you need broader access, contact your system administrator.',
      },
      {
        question: 'What does the working pattern affect?',
        answer: 'The working pattern determines the available hours figure used in utilisation calculations. It does not restrict what a user can enter in their timesheet.',
      },
    ],
    relatedPages: [
      { label: 'Approvals', path: '/approvals' },
      { label: 'Utilisation Report', path: '/reports/utilisation' },
      { label: 'Timesheet Reports', path: '/reports/timesheet' },
    ],
  },

  '/projects/active': {
    pageTitle: 'Active Projects',
    description: 'View the projects currently available for time logging, sourced from the project management system.',
    icon: Briefcase,
    functionality: {
      overview:
        'The Active Projects page shows a read-only list of all projects that employees can currently log time against. Projects are managed in the project management system and synchronised here automatically — no changes can be made from this page.',
      keyFeatures: [
        { label: 'Search', detail: 'Finds projects by name, project code, or client name as you type.' },
        { label: 'Quick Filter', detail: 'Filters the list by project status or priority.' },
        { label: 'View Toggle', detail: 'Switch between a Grid view (table) and a Cards view (visual project cards).' },
      ],
      sectionsAndFields: [
        { name: 'Total Projects Card', description: 'Shows total project count with active count breakdown. Clickable — resets all filters.' },
        { name: 'Team Members Card', description: 'Shows the total number of team members across all projects.' },
        { name: 'At Risk Card', description: 'Shows the count of at-risk projects with an action required badge. Clickable — filters the list to at-risk projects only.' },
        { name: 'High Priority Card', description: 'Shows the count of high priority projects and percentage of portfolio. Clickable — filters to high priority projects only.' },
        { name: 'Search Bar', description: 'Filters the project list by project name, code, or client name. Includes a clear button when a search term is entered.' },
        { name: 'Status Filter', description: 'A dropdown to filter by All Statuses, Active, At Risk, or On Hold.' },
        { name: 'Priority Filter', description: 'A dropdown to filter by All Priorities, High, Medium, or Low.' },
        { name: 'Sort By', description: 'Sorts by Project Name, Project Code, Client Name, Start Date, or End Date.' },
        { name: 'Active Filter Chips', description: 'Removable badges shown when filters are active — click X on any chip to remove that filter individually.' },
        { name: 'Export Button', description: 'Downloads the current project list.' },
        { name: 'View Toggle', description: 'Switches between Grid (table) and Cards (visual card) layouts.' },
        { name: 'Project Card / Row', description: 'Displays project name, code, client, status badge, priority badge, billable flag, and timeline. Click to open the full detail dialog.' },
      ],
      useCases: [
        'Checking which projects are currently available for employees to log time against.',
        'Looking up a project code for use in reporting.',
        'Verifying whether a project is marked as billable or non-billable.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Use the search bar or filters to find the project you are looking for. Click any project card or row to view its full details including team members, status, and priority.',
      steps: [
        {
          title: 'How do I find a specific project?',
          items: [
            { text: 'Use the search bar at the top of the page.' },
            { text: 'Type a project name or project code.' },
            { text: 'The list filters as you type. Click the project to view its details.' },
          ],
        },
      ],
      proTips: [
        'If a project you need is not in the list, it may not yet be active in the project management system — raise it with your project manager.',
        'Project codes in this list match those used in timesheet reports and exports.',
      ],
    },
    faq: [
      {
        question: 'Can I add or edit a project from this page?',
        answer: 'No. Projects are managed in the project management system and synchronised here automatically. Contact your project manager or administrator to make any changes.',
      },
      {
        question: 'Why is a project I need not showing in my timesheet?',
        answer: 'The project may not yet be active in the project management system, or it may not have been synchronised yet. Check this page to confirm it is listed — if it is missing, contact your administrator.',
      },
      {
        question: 'What does the Billable flag mean?',
        answer: 'It indicates whether hours logged to that project are counted as billable in utilisation and cost reports. This is set in the project management system and cannot be changed here.',
      },
    ],
    relatedPages: [
      { label: 'Timesheet Reports', path: '/reports/timesheet' },
      { label: 'Utilisation Report', path: '/reports/utilisation' },
    ],
  },

  '/integrations': {
    pageTitle: 'Integrations',
    description: 'Manage connections between Access Time and your financial, HR, project, and planner systems — configured per company.',
    icon: Zap,
    functionality: {
      overview:
        'The Integrations page lists all companies configured in Access Time and shows the connection status of each system integration at a glance. Each company can be linked to a Financial system (which receives approved timesheet exports), an HR system (which provides absence data), a Project management system, and a Planner system. Click any company row to open its configuration sheet and manage the integration settings for that company.',
      keyFeatures: [
        { label: 'Search & Filter', detail: 'Search by company name or code, and filter the list by Active or Inactive company status.' },
        { label: 'Add Company', detail: 'Administrators can register a new company and configure its integrations via the Add Company button.' },
      ],
      sectionsAndFields: [
        { name: 'Company column', description: 'The company name and short code. Click anywhere on the row to open the company configuration sheet.' },
        { name: 'Financial column', description: 'Shows which financial system is connected and its connection status. Approved timesheet data is exported to this system.' },
        { name: 'HR column', description: 'Shows which HR system is connected and its connection status. Absence data is pulled from this system into Access Time.' },
        { name: 'Project column', description: 'Shows which project management system is connected and its connection status.' },
        { name: 'Planner column', description: 'Shows which planner system is connected and its connection status.' },
        { name: 'Users column', description: 'The number of users associated with this company.' },
        { name: 'Status badge', description: 'Indicates whether the company is Active or Inactive within Access Time.' },
        { name: 'Search bar', description: 'Filters the table by company name or company code.' },
        { name: 'Status filter', description: 'Dropdown to show All, Active, or Inactive companies.' },
        { name: 'Add Company button', description: 'Opens the company creation sheet. Requires administrator permissions.' },
      ],
      useCases: [
        'Checking at a glance which companies have a financial system connected and which still need configuring.',
        'Reviewing whether the HR integration is active for a company to confirm absence data is being synchronised.',
        'Adding a new company and setting up its financial and HR integrations before going live.',
        'Identifying a company showing an Error status on its financial integration so you can investigate and reconnect it.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Use the search bar or status filter to find the company you need, then click its row to open the integration configuration. Use the Add Company button to connect a new company for the first time.',
      steps: [
        {
          title: 'How do I configure an integration for a company?',
          items: [
            { text: 'Find the company in the table using the search bar or status filter.' },
            { text: 'Click anywhere on the company row to open the configuration sheet on the right.' },
            { text: 'In the sheet, select the integration type you want to set up — Financial, HR, Project, or Planner.' },
            { text: 'Choose the product from the dropdown.' },
            { text: 'Enter the required connection details such as instance URL or API credentials.' },
            { text: 'Save the configuration. The table updates to show the connected product and a Connected status badge.', note: 'If the status shows Error after saving, check that the credentials are correct and the external system is reachable.' },
          ],
        },
        {
          title: 'How do I add a new company?',
          items: [
            { text: 'Click Add Company in the top right.' },
            { text: 'Enter the company name and a unique company code.' },
            { text: 'Configure any integrations needed for the new company in the same sheet.' },
            { text: 'Click Save. The new company appears in the table.', tip: 'Company codes must be unique across all companies — use a short, recognisable abbreviation.' },
          ],
        },
        {
          title: 'How do I check what data each integration provides?',
          items: [
            { text: 'Financial integrations receive approved timesheet exports from Access Time — they do not push data into Access Time.' },
            { text: 'HR integrations provide absence data to Access Time — employee records are not imported via this integration.' },
            { text: 'Project integrations link Access Time to your project management system for project and task data.' },
            { text: 'Planner integrations connect Access Time to scheduling and resource planning tools.' },
          ],
        },
      ],
      proTips: [
        'Use the Status filter set to Active to keep the table focused on live companies during day-to-day administration.',
        'An Error badge on an integration usually means the API credentials have expired or the external system URL has changed — open the company sheet to update them.',
        'Keep integration credentials in a secure password manager and rotate them in line with your security policy.',
      ],
    },
    faq: [
      {
        question: 'What data does the HR integration bring into Access Time?',
        answer: 'The HR integration pulls absence data — such as annual leave and sick leave — into Access Time. It does not import employee records; those are managed separately.',
      },
      {
        question: 'What data is sent to the financial system?',
        answer: 'Approved timesheet data is exported to the connected financial system. This is used for billing and finance reporting — it is not a payroll feed.',
      },
      {
        question: 'Why does a company show "Not Connected" for an integration?',
        answer: 'Not Connected means no product has been selected or configured for that integration type. Click the company row and set up the integration in the configuration sheet.',
      },
      {
        question: 'What does an Error status mean on an integration?',
        answer: 'An Error means the connection to the external system has failed — usually because credentials have expired or the system URL has changed. Open the company sheet and update the connection details.',
      },
      {
        question: 'Can I have different financial systems for different companies?',
        answer: 'Yes. Each company has its own independent integration configuration, so you can connect different financial, HR, project, and planner products per company.',
      },
    ],
    relatedPages: [
      { label: 'Settings', path: '/settings/general' },
      { label: 'Team Members', path: '/team/members' },
    ],
  },

  '/approvals/delegations': {
    pageTitle: 'My Delegations',
    description: 'Set up and manage who approves timesheets on your behalf while you are away.',
    icon: ArrowLeftRight,
    functionality: {
      overview:
        'My Delegations lets you temporarily hand your approval responsibilities to a colleague. When a delegation is active, your delegate receives approval requests in your place for the duration you specify. You can set up multiple delegations and revoke them at any time.',
      keyFeatures: [
        { label: 'Set Up Delegation', detail: 'Opens a dialog to assign a colleague as your approval delegate for a specific date range.' },
        { label: 'Status Filter', detail: 'Filters the delegation list to show only Active, Expired, or Revoked entries.' },
        { label: 'Revoke', detail: 'Cancels an active delegation immediately — only visible on active rows.' },
      ],
      sectionsAndFields: [
        { name: 'Set Up Delegation', description: 'Button in the top right — opens the dialog to create a new delegation.' },
        { name: 'Search', description: 'Text input to filter the list by delegate name.' },
        { name: 'Status Filter', description: 'Dropdown to filter the delegation list by All, Active, Expired, or Revoked.' },
        { name: 'Refresh', description: 'Button to reload the delegation list and pick up the latest data.' },
        { name: 'Export', description: 'Downloads the current delegation list as a CSV file.' },
        { name: 'Delegate', description: 'The colleague assigned to receive and action approval requests on your behalf.' },
        { name: 'Date Range', description: 'The start and end dates during which the delegation is in effect.' },
        { name: 'Duration', description: 'The number of calendar days the delegation spans, calculated automatically.' },
        { name: 'Status', description: 'Current state of the delegation — Active, Expired, or Revoked.' },
        { name: 'Notes', description: 'Optional free-text field to record the reason for the delegation.' },
        { name: 'Revoke Reason', description: 'The reason entered when a delegation was cancelled early — only shown on revoked rows.' },
        { name: 'Revoke', description: 'Button shown on active rows only — cancels the delegation immediately.' },
      ],
      useCases: [
        'Setting up annual leave cover so timesheets are not blocked while you are away.',
        'Delegating approvals during a period of reduced availability such as a conference or travel.',
        'Reviewing past delegations to confirm cover was in place for a specific period.',
        'Revoking a delegation early because you have returned sooner than expected.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Click Set Up Delegation to assign a colleague as your delegate and set the date range you will be unavailable. You can revoke any active delegation at any time from this page.',
      steps: [
        {
          title: 'How do I set up a delegation?',
          items: [
            { text: 'Click Set Up Delegation in the top right.' },
            { text: 'Select your delegate by typing the first few characters of their name.' },
            { text: 'Set the Start Date and End Date for the delegation period.', note: 'The delegation becomes active at the start of the start date and expires at the end of the end date.' },
            { text: 'Add an optional Note to explain the reason for the delegation.' },
            { text: 'Click Save. The delegation is now active and your delegate will receive approval notifications.', tip: 'You can also reassign any pending approvals on your list.' },
          ],
        },
        {
          title: 'How do I revoke a delegation?',
          items: [
            { text: 'Find the active delegation in the list.' },
            { text: 'Click the Revoke button on the right of the row.' },
            { text: 'Enter an optional reason for revoking.' },
            { text: 'Click Confirm. The delegation status changes to Revoked immediately.' },
            { note: 'Revoking a delegation is immediate. Any pending approvals that were routed to your delegate will need to be re-assigned by your administrator.' },
          ],
        },
        {
          title: 'How do I export my delegation history?',
          items: [
            { text: 'Use the Status Filter to select which delegations you want to export (or leave it on All statuses).' },
            { text: 'Click the Export button in the toolbar.' },
            { text: 'A CSV file downloads automatically with all visible delegation records.', tip: 'The filename includes today\'s date for easy filing.' },
          ],
        },
      ],
      proTips: [
        'You can have more than one active delegation at a time — useful if you need two colleagues to share cover.',
        'The delegation does not give your delegate access to your account — it only routes approval requests to them.',
        'Expired delegations remain in the list for audit purposes. Use the Status filter to hide them from the active view.',
      ],
    },
    faq: [
      {
        question: 'Can I delegate to more than one person at the same time?',
        answer: 'Yes. You can create multiple active delegations with overlapping date ranges. All delegates will receive approval notifications during any period where their delegation is active.',
      },
      {
        question: 'What happens to approvals that were sent to my delegate after I revoke the delegation?',
        answer: 'Revoking a delegation stops new approvals being routed to your delegate but does not automatically reassign approvals that were already sent. Contact your administrator to reassign those manually.',
      },
      {
        question: 'Can my delegate see all my timesheets or just the ones needing approval?',
        answer: 'Your delegate only receives approval requests — they do not gain access to your personal timesheet or account settings.',
      },
      {
        question: 'How do I know if my delegation is currently active?',
        answer: 'Active delegations show an Active badge in the Status column. You can use the Status filter to show only active delegations at a glance.',
      },
      {
        question: 'What does "Expired" mean on a delegation?',
        answer: 'Expired means the delegation date range has passed and the delegation ended automatically. No action is needed — expired delegations are kept for audit purposes only.',
      },
    ],
    relatedPages: [
      { label: 'Approvals', path: '/approvals' },
      { label: 'Delegation Settings', path: '/settings/approvals/delegation' },
      { label: 'Notifications', path: '/settings/notifications' },
    ],
  },

  '/settings/general': {
    pageTitle: 'General Settings',
    description: 'Configure organisation-wide timesheet rules, working hours, deadlines, and policy enforcement.',
    icon: Settings,
    functionality: {
      overview:
        'General Settings is the central configuration page for your organisation\'s timesheet system. Administrators use it to define the working week, submission deadlines, time entry rules, utilisation targets, and policy enforcement behaviour. Changes here affect all users across the organisation.',
      keyFeatures: [
        { label: 'Run Health Check', detail: 'Runs a diagnostic across all settings to identify conflicts or missing configuration.' },
      ],
      sectionsAndFields: [
        { name: 'Working Hours & Policies tab', description: 'Configure contracted hours per day, working days per week, policy enforcement rules, policy limits, and approval screen columns.' },
        { name: 'Submissions & Deadlines tab', description: 'Set the submission deadline day/time, grace period after the deadline, and recall behaviour.' },
        { name: 'Time Entry Rules tab', description: 'Set week start day; toggle Role and Cost Centre visibility and requirement; configure Description requirement and type; set project selection scope; show archived projects; show project time remaining; minimum entry duration; time rounding; allow negative hours; historical entry limit; allow future-dated entries; Copy Previous Week settings; Smart Auto-Fill; AI Insights.' },
        { name: 'Work Type tab', description: 'Enable or disable work type tracking for the organisation and manage the list of work type definitions.' },
        { name: 'Utilisation tab', description: 'Set the target billable utilisation percentage and the date range used for calculations.' },
        { name: 'Run Health Check button', description: 'Opens a diagnostic that scans all settings for conflicts and missing configuration. Click a finding to jump to the affected setting.' },
        { name: 'Save Settings button', description: 'Saves changes on the active tab. Disabled if there are no unsaved changes or if you do not have edit permissions for that tab.' },
        { name: 'Cancel button', description: 'Discards unsaved changes on the active tab and reverts to the last saved state.' },
        { name: 'Unsaved Changes indicator', description: 'A dot appears on the tab label and a banner appears within the tab whenever there are unsaved changes.' },
      ],
      useCases: [
        'Changing the weekly submission deadline.',
        'Making the Description field mandatory on all time entries.',
        'Changing billable utilisation target.',
        'Configuring overtime to require separate manager approval.',
        'Running a health check before a new financial period to catch configuration issues.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Select the tab relevant to what you need to configure — Working Hours, Submissions, Time Entry, Work Types, or Utilisation. Make your changes and click Save at the bottom of the page to apply them.',
      steps: [
        {
          title: 'How do I change the timesheet submission deadline?',
          items: [
            { text: 'Navigate to the Submissions & Deadlines section.' },
            { text: 'Select the deadline day from the Day dropdown.' },
            { text: 'Set the deadline time using the time field.' },
            { text: 'Optionally set a grace period to allow late submissions within a defined window.' },
            { text: 'Click Save Settings.', note: 'The new deadline applies from the next working week — it does not affect the current open timesheet period.' },
          ],
        },
        {
          title: 'How do I make the Description field mandatory?',
          items: [
            { text: 'Scroll to the Time Entry Rules section.' },
            { text: 'Find the Description requirement dropdown.' },
            { text: 'Select Always required.' },
            { text: 'Click Save Settings. Employees will be prompted to enter a description on every time entry going forward.', tip: 'Existing entries are not affected — the rule applies to new entries only.' },
          ],
        },
        {
          title: 'How do I run a Settings Health Check?',
          items: [
            { text: 'Click the Health Check button in the page header.' },
            { text: 'The system scans all settings for conflicts and missing values.' },
            { text: 'Review the findings listed in the Health Check dialog.' },
            { text: 'Click on a finding to navigate directly to the affected setting.' },
            { note: 'The health check is read-only — it does not make any changes automatically.' },
          ],
        },
      ],
      proTips: [
        'Run the Health Check at the start of each financial year to ensure settings are aligned with any policy changes.',
        'If you change the Week Start Day, expect report date groupings to shift — communicate this to your team before making the change.',
        'The Conflict Resolution dialog appears automatically if two settings contradict each other — always resolve conflicts before saving.',
        'Saving settings with validation errors is blocked — scroll to the top to see the error summary if Save is disabled.',
      ],
    },
    faq: [
      {
        question: 'What happens if I change the week start day?',
        answer: 'Changing the week start day affects how timesheets are grouped in the timesheet grid and how weekly deadlines are calculated. Historical data is not recalculated — the change applies to new timesheet periods only.',
      },
      {
        question: 'Can I set different deadlines for different teams?',
        answer: 'General Settings applies one deadline to the whole organisation. Team-specific deadlines are not currently supported.',
      },
      {
        question: 'What does the grace period setting do?',
        answer: 'The grace period allows employees to submit their timesheet after the deadline without triggering a late submission flag. For example, a Friday 5pm deadline with a 2-hour grace period allows submission until 7pm on Friday.',
      },
      {
        question: 'Will changing the utilisation target affect historical reports?',
        answer: 'No. The utilisation target is used for display indicators only (e.g. colour-coded badges). Historical utilisation percentages in reports are based on logged hours and contracted hours, not on the target value.',
      },
    ],
    relatedPages: [
      { label: 'Notifications', path: '/settings/notifications' },
      { label: 'Roles & Rates', path: '/settings/roles' },
      { label: 'Approval Workflows', path: '/settings/approvals' },
      { label: 'Screen Settings', path: '/settings/screens' },
    ],
  },

  '/settings/notifications': {
    pageTitle: 'Notification Settings',
    description: 'Configure automated reminders and digest feeds for timesheet submissions, approvals, and missing timesheets.',
    icon: Bell,
    functionality: {
      overview:
        'Notification Settings controls all automated Access Evo feed notifications and reminders sent by the system. Administrators configure when reminders are sent, who receives them, and how often digest summaries are delivered to managers. Delegation notifications are also configured here.',
      keyFeatures: [
        { label: 'Submission Reminders', detail: 'Sends automated notifications to employees who have not submitted their timesheet by a set time before the deadline.' },
        { label: 'Approval Reminders', detail: 'Notifies managers of timesheets that have been waiting for approval beyond a defined threshold.' },
        { label: 'Missing Timesheet Reminders', detail: 'Sends alerts for employees who have not submitted a timesheet at all for a given period.' },
        { label: 'Delegation Notifications', detail: 'Controls which notifications are sent when delegations are created, activated, or revoked.' },
      ],
      sectionsAndFields: [
        { name: 'Submission Reminder Settings', description: 'Enable/disable employee submission reminders and set how many hours before the deadline the reminder is sent.' },
        { name: 'Approval Reminder Settings', description: 'Enable/disable manager approval reminders and set how many hours a submission can wait before a reminder is triggered.' },
        { name: 'Missing Timesheet Reminder Settings', description: 'Enable/disable alerts for employees with no timesheet submitted for the current period.' },
        { name: 'Manager Weekly Digest Settings', description: 'Enable/disable the weekly digest and set the day and time it is delivered to managers.' },
        { name: 'Delegation Notification Settings', description: 'Independent toggles for notifications when a delegation is created, started, expiring soon, or revoked.' },
        { name: 'Save Settings button', description: 'Commits all notification changes. Only enabled when there are unsaved changes.' },
      ],
      useCases: [
        'Enabling a Friday morning reminder for employees who have not yet submitted their timesheet.',
        'Setting up a manager digest every Monday morning to review the previous week\'s submission rates.',
        'Configuring approval reminders so managers are chased after 24 hours of inaction.',
        'Turning off missing timesheet alerts during a public holiday period to avoid unnecessary noise.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Toggle the notification types you want to enable, then set the timing for each. Click Save Settings at the bottom once you are happy with your configuration.',
      steps: [
        {
          title: 'How do I enable timesheet submission reminders?',
          items: [
            { text: 'Scroll to the Timesheet Reminder Settings section.' },
            { text: 'Toggle Enable Timesheet Reminders to on.' },
            { text: 'To change the specifics of this alert amend the details in General > Submission & Deadlines.' },
            { text: 'Click Save.' },
          ],
        },
        {
          title: 'How do I set up the manager digest feed?',
          items: [
            { text: 'Scroll to the Manager Digest Settings section.' },
            { text: 'Toggle Enable Manager Digest to on.' },
            { text: 'Select the day of the week the digest should be sent.' },
            { text: 'Set the time for delivery.' },
            { text: 'Click Save.', tip: 'Set the digest feed to arrive on the same day as your team standup so managers have up-to-date submission data to hand.' },
          ],
        },
        {
          title: 'How do I configure delegation notifications?',
          items: [
            { text: 'Scroll to the Delegation Notification Settings section.' },
            { text: 'Toggle each notification type on or off — Delegation Created, Delegation Started, Delegation Revoked.' },
            { text: 'Click Save.' },
          ],
        },
      ],
      proTips: [
        'Set the submission reminder to trigger 4–8 hours before the deadline — early enough for employees to act, late enough that most will have already submitted.',
        'Disable missing timesheet alerts for the week of a public holiday to avoid false positives.',
        'Manager digest feeds are only sent if there is at least one team member with an outstanding or submitted timesheet — no notification is sent if everything is fully approved.',
      ],
    },
    faq: [
      {
        question: 'Can I set different reminder times for different teams?',
        answer: 'No. Notification settings apply to the whole organisation. Team-specific notification rules are not currently supported.',
      },
      {
        question: 'Will employees receive reminders if their timesheet is already submitted?',
        answer: 'No. Access Evo feed reminders are only sent to employees whose timesheet status is Not Started or In Progress at the time the reminder is triggered.',
      },
      {
        question: 'How do I stop all notifications temporarily?',
        answer: 'Toggle off each notification type individually and click Save. There is no single master off switch — each notification type must be disabled separately.',
      },
      {
        question: 'What does the Delegation Started notification do?',
        answer: 'The Delegation Started notification sends an Access Evo feed notification to the delegate on the first day their delegation becomes active, reminding them that they are now responsible for approvals.',
      },
    ],
    relatedPages: [
      { label: 'General Settings', path: '/settings/general' },
      { label: 'My Delegations', path: '/approvals/delegations' },
      { label: 'Delegation Settings', path: '/settings/approvals/delegation' },
    ],
  },

  '/settings/screens': {
    pageTitle: 'Screen Settings',
    description: 'Control whether each screen in the application uses live data from the API or demo data for testing.',
    icon: LayoutDashboard,
    functionality: {
      overview:
        'Screen Settings allows administrators to toggle individual screens between Live mode (real API data) and Demo mode (pre-loaded sample data). This is useful during onboarding, testing, or demonstrations without affecting production data.',
      keyFeatures: [
        { label: 'Statistics Cards', detail: 'Three cards at the top showing Total Screens, Demo Mode count, and Live Mode count with percentages.' },
        { label: 'Info Banner', detail: 'Explains what per-screen configuration means and the phased rollout use case.' },
        { label: 'Quick Actions', detail: 'Two buttons — Set All to Demo and Set All to Live — to apply a mode to every screen at once.' },
        { label: 'Per-Screen Toggle', detail: 'Each screen row has a toggle switch to independently set it to Demo or Live mode.' },
        { label: 'Category Grouping', detail: 'Screens are grouped by functional area in separate cards for easier navigation.' },
      ],
      sectionsAndFields: [
        { name: 'Total Screens card', description: 'Shows the total count of configurable screens in the application.' },
        { name: 'Demo Mode card', description: 'Shows how many screens are currently in Demo mode with a percentage.' },
        { name: 'Live Mode card', description: 'Shows how many screens are currently in Live mode with a percentage.' },
        { name: 'Info Banner', description: 'Explains Demo vs Live mode and the phased rollout pattern.' },
        { name: 'Set All to Demo button', description: 'Sets every screen to Demo mode in one action.' },
        { name: 'Set All to Live button', description: 'Sets every screen to Live mode in one action.' },
        { name: 'Category Group card', description: 'A card per functional area listing all screens in that area with their current mode badge and toggle.' },
        { name: 'Screen Mode Toggle', description: 'A toggle switch on each screen row to switch between Demo and Live mode independently.' },
        { name: 'Mode Badge', description: 'Colour-coded badge on each row — amber for Demo, green for Live.' },
      ],
      useCases: [
        'Switching the Timesheets screen to Live mode when going live with production data.',
        'Keeping Reports screens in Demo mode during a stakeholder demonstration.',
        'Resetting all screens to Demo mode before a training session.',
        'Checking which screens are still in Demo mode before go-live.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Each screen can be toggled between Demo and Live mode independently. Use Set All to Demo or Set All to Live to switch everything at once, or toggle individual screens as needed.',
      steps: [
        {
          title: 'How do I switch a screen to Live mode?',
          items: [
            { text: 'Find the screen in the appropriate category group.' },
            { text: 'Click the mode toggle next to the screen name.' },
            { text: 'Review the confirmation dialog — it shows the screen name and the mode you are switching to.' },
            { text: 'Click Confirm. The badge updates to Live immediately.', note: 'Switching to Live mode means the screen will now read and write real data. Ensure your API connection is configured before switching.' },
          ],
        },
        {
          title: 'How do I reset all screens to Demo mode?',
          items: [
            { text: 'Click the Reset All button in the page header.' },
            { text: 'Select Demo from the options.' },
            { text: 'Confirm the action.', tip: 'Use Reset All to Demo before running a training session to ensure no production data is visible.' },
          ],
        },
      ],
      proTips: [
        'Switch screens to Live mode one at a time during go-live to validate each connection before proceeding to the next.',
        'Demo mode is safe — no data is written to the API, so it is the right mode for training and demonstrations.',
        'The Statistics Bar gives you a quick count of how many screens are Live — use this as a go-live checklist.',
      ],
    },
    faq: [
      {
        question: 'What is the difference between Demo and Live mode?',
        answer: 'Demo mode displays pre-loaded sample data and does not connect to the API — no real data is read or written. Live mode connects to the real API and displays actual production data.',
      },
      {
        question: 'Can I switch screens to Live mode one at a time?',
        answer: 'Yes. Each screen has an independent toggle. You can switch screens to Live mode individually, which is the recommended approach during a phased go-live.',
      },
      {
        question: 'What happens to changes I made in Demo mode when I switch to Live?',
        answer: 'Changes made in Demo mode are not saved to the API — they are lost when you switch to Live mode. Demo mode data resets each session.',
      },
      {
        question: 'Will users see a difference when I switch a screen from Demo to Live?',
        answer: 'Yes. The data displayed will change from sample data to real data immediately. Inform users before switching screens to Live during business hours.',
      },
    ],
    relatedPages: [
      { label: 'General Settings', path: '/settings/general' },
      { label: 'Integrations', path: '/integrations' },
    ],
  },

  '/settings/roles': {
    pageTitle: 'Roles & Rates',
    description: 'View the billing roles and charge-out rates used in timesheets, cost reporting, and utilisation calculations. Roles are managed in the Resource Manager application.',
    icon: Briefcase,
    functionality: {
      overview:
        'The Roles & Rates page displays the billing roles and charge-out rates used across timesheets, project cost reporting, and utilisation calculations. Roles themselves are created and managed in the Resource Manager application — this page allows administrators to configure rate overrides at the project or employee level.',
      keyFeatures: [
        { label: 'Search', detail: 'Filters the role list by name as you type.' },
        { label: 'Show Archived toggle', detail: 'Toggles between showing only active roles and including archived roles in the list.' },
      ],
      sectionsAndFields: [
        { name: 'Roles & Rates tab', description: 'Searchable table of all billing roles showing Role, Description, Cost Rate (Hour), Cost Rate (Day), Bill Rate (Hour), Bill Rate (Day), and Margin.' },
        { name: 'Project Overrides tab', description: 'Table of project-specific rate overrides — a different charge-out rate for a role on a specific project.' },
        { name: 'Employee Overrides tab', description: 'Table of employee-specific rate overrides — an individual billed at a rate different from their role default.' },
        { name: 'Search bar', description: 'Filters the role list by role name.' },
        { name: 'Show Archived button', description: 'Toggles whether archived roles are included in the list.' },
        { name: 'Role Name', description: 'The display name of the role as it appears in timesheet entries and reports.' },
        { name: 'Cost Rate (Hour / Day)', description: 'The internal cost for this role — hourly rate and the equivalent daily rate (hourly × 8).' },
        { name: 'Bill Rate (Hour / Day)', description: 'The charge-out rate for this role — hourly rate and the equivalent daily rate.' },
        { name: 'Margin', description: 'The calculated margin percentage between bill rate and cost rate for this role.' },
      ],
      useCases: [
        'Checking the rate override applied to a specific employee before raising an invoice.',
        'Reviewing the default charge-out rates synced from Resource Manager before running a cost report.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Use the tabs to switch between Roles & Rates, Project Overrides, and Employee Overrides. Click any row to view more details, or use the Project and Employee Override tabs to view specific assignments.',
      steps: [
        {
          title: 'How do I set a rate override?',
          items: [
            { text: 'You cannot set rate overrides within Timetrack, everything is read-only content from your Projects. For more details contact your administrator.' },
          ],
        },
      ],
      proTips: [
        'To add, edit, or archive roles, use the Resource Manager application — changes will sync through to this page automatically.',
      ],
    },
    faq: [
      {
        question: 'Where do I add, edit, or archive roles?',
        answer: 'Role management (adding, editing, and archiving) is done in the Resource Manager application. This page displays roles synced from Resource Manager and allows rate overrides only.',
      },
      {
        question: 'What happens to historical timesheet data if a role\'s rate changes in Resource Manager?',
        answer: 'Changing the default rate on a role does not recalculate historical billing data. The new rate applies to future timesheet entries only.',
      },
      {
        question: 'Can I have more than one rate for the same role?',
        answer: 'Yes — use Project Overrides to apply a different rate for a specific role on a specific project, or Employee Overrides to set a rate for an individual person.',
      },
      {
        question: 'What does the Role Code field do?',
        answer: 'The Role Code is a short identifier used in CSV exports and integrations. It does not appear to employees in the timesheet — it is for administrator and system use only.',
      },
    ],
    relatedPages: [
      { label: 'Team Members', path: '/team/members' },
      { label: 'General Settings', path: '/settings/general' },
      { label: 'Utilisation Report', path: '/reports/utilisation' },
    ],
  },

  '/settings/roles-permissions': {
    pageTitle: 'User Roles & Permissions',
    description: 'Define and manage the permission sets that control what each user can see and do in the application.',
    icon: Shield,
    functionality: {
      overview:
        'User Roles & Permissions is where administrators create and manage the permission profiles assigned to users. Each role defines which pages, actions, and data a user can access. Roles can be duplicated to create variants and deleted when no longer needed.',
      keyFeatures: [
        { label: 'Create Role', detail: 'Opens the role editor to define a new permission profile from scratch.' },
        { label: 'Edit Role', detail: 'Opens the role editor for an existing role to modify its permissions.' },
      ],
      sectionsAndFields: [
        { name: 'Create Role button', description: 'Opens the role editor to define a new permission profile.' },
        { name: 'Search bar', description: 'Filters the role list by role name as you type.' },
        { name: 'Role Name', description: 'The display name of the permission profile.' },
        { name: 'Description', description: 'A plain-language summary of what the role is intended for.' },
        { name: 'Users column', description: 'The number of users currently assigned to this role, shown as a badge.' },
        { name: 'Status column', description: 'Whether the role is Active or Inactive.' },
        { name: 'Type column', description: 'Platform roles are built-in and cannot be deleted. Custom roles are administrator-created.' },
        { name: 'Edit button', description: 'Opens the role permission editor for the selected role.' },
        { name: 'Duplicate button', description: 'Creates a copy of the role pre-filled with the same permissions.' },
        { name: 'Delete button', description: 'Deletes the role — disabled on Platform roles and roles with users assigned.' },
      ],
      useCases: [
        'Creating a read-only Report Viewer role for finance staff who need to see reports but not manage timesheets.',
        'Duplicating the Manager role to create a Senior Manager role with additional admin permissions.',
        'Auditing which roles exist before assigning a new employee to the system.',
        'Deleting a legacy role that was replaced by a newer profile.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Click Create Role to add a new role, or click an existing role to edit its permissions. Use Duplicate to base a new role on an existing one — it is faster than starting from scratch.',
      steps: [
        {
          title: 'How do I create a new role?',
          items: [
            { text: 'Click Create Role in the top right.' },
            { text: 'Enter a Role Name and Description.' },
            { text: 'Configure the permissions in each category — toggle individual permissions on or off.' },
            { text: 'Click Save. The role is immediately available for assignment to users.', tip: 'Use the Duplicate function on an existing role if you want to start from a known configuration rather than from scratch.' },
          ],
        },
        {
          title: 'How do I duplicate a role?',
          items: [
            { text: 'Find the role you want to copy in the list.' },
            { text: 'Click the Duplicate button on the role row.' },
            { text: 'Confirm the duplication.', note: 'The duplicate is created with the same permissions and the name "Copy of [Role Name]". Rename it immediately to avoid confusion.' },
            { text: 'Click the Edit button on the new copy to rename it and adjust permissions.' },
          ],
        },
        {
          title: 'How do I delete a role?',
          items: [
            { text: 'Ensure no users are assigned to the role — the Users Assigned column must show 0.' },
            { text: 'Click the Delete button on the role row.' },
            { text: 'Confirm the deletion.', note: 'If users are still assigned to the role, the Delete button is disabled. Reassign those users first.' },
          ],
        },
      ],
      proTips: [
        'Keep roles aligned with job functions, not individual people — avoid creating a "John Smith" role.',
        'Use the Description field to document who the role is designed for and what its key permissions are.',
        'Review the Users Assigned count regularly — roles with zero users assigned may be candidates for deletion.',
        'Start with the minimum permissions needed and add more if users report they cannot complete a task.',
      ],
    },
    faq: [
      {
        question: 'Can I delete a role that has users assigned to it?',
        answer: 'No. The Delete button is disabled if any users are currently assigned to the role. Reassign all users to another role first, then delete.',
      },
      {
        question: 'What happens to a user\'s access if I edit the role they are assigned to?',
        answer: 'Permission changes take effect immediately. The user may need to refresh their browser to see the updated access.',
      },
      {
        question: 'How do I assign a role to a user?',
        answer: 'Role assignment is done from the Team Members page — open the employee record and update their assigned role.',
      },
      {
        question: 'Can a user have more than one role?',
        answer: 'Each user is assigned one role at a time. If a user needs access from two different roles, create a combined role that includes all the required permissions.',
      },
    ],
    relatedPages: [
      { label: 'Team Members', path: '/team/members' },
      { label: 'General Settings', path: '/settings/general' },
      { label: 'Roles & Rates', path: '/settings/roles' },
    ],
  },

  '/settings/approvals': {
    pageTitle: 'Approval Workflows',
    description: 'Create and manage the multi-stage approval workflows that control how timesheets are reviewed and approved.',
    icon: ClipboardCheck,
    functionality: {
      overview:
        'Approval Workflows is where administrators design the approval chains used across the organisation. A workflow is made up of one or more stages, each representing a step in the approval process. You define who approves at each stage, what happens when they approve or reject, and whether any smart routing rules apply. Once built, workflows are assigned to employees — every timesheet they submit will follow that workflow.',
      keyFeatures: [
        { label: 'Quick Start Templates', detail: 'Three pre-configured templates — Single Manager, Two-Stage, and Auto-Approve — to create a working workflow in seconds. Shown prominently when no workflows exist, or as a compact banner when workflows are present.' },
        { label: 'New Workflow button', detail: 'Creates a new empty workflow with a name and description. Opens the Workflow Details page automatically to start adding stages.' },
        { label: 'Search', detail: 'Filters the workflow list by name as you type.' },
      ],
      sectionsAndFields: [
        { name: 'Quick Start Templates', description: 'Single Manager (1 stage), Two-Stage (2 stages), and Auto-Approve (no review) templates to get started quickly.' },
        { name: 'New Workflow button', description: 'Opens a dialog to name and describe a new workflow, then navigates to the Workflow Details page.' },
        { name: 'Search bar', description: 'Filters the workflow list by name.' },
        { name: 'Status Filter', description: 'Dropdown to show All Statuses, Active, Draft, or Inactive workflows.' },
        { name: 'Active / Draft / Inactive count cards', description: 'Summary stat cards showing the count of each workflow status — visible when 3 or more workflows exist.' },
        { name: 'Workflow Name', description: 'The display name of the workflow.' },
        { name: 'Stages count', description: 'The number of sequential approval stages in the workflow.' },
        { name: 'Assigned Employees', description: 'How many employees are currently using this workflow.' },
        { name: 'Status Badge', description: 'Whether the workflow is Draft, Active, or Inactive.' },
        { name: 'Assign button', description: 'Opens a dialog to link the workflow to specific employees.' },
        { name: 'Add Stage button', description: 'Opens the Stage Panel to configure a new stage.' },
        { name: 'Stage card', description: 'Shows stage name, approver type, rule count, and status. Click Edit to open the Stage Panel.' },
        { name: 'List / Visual toggle', description: 'On the Workflow Details page, switches between a stage list and a visual flow diagram.' },
      ],
      useCases: [
        'Creating a two-stage workflow where the direct manager approves first and a department head approves second.',
        'Building a single-stage workflow for contractors with a specific named approver.',
        'Setting up a smart rule so timesheets over 50 hours are escalated to a senior approver automatically.',
        'Duplicating the standard workflow to create a simplified one-stage version for part-time staff.',
        'Deactivating a workflow that was set up for a project that has now ended.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Click New Workflow to create one from scratch, or use a Quick Start Template to get up and running faster. Keep a workflow in Draft while building it — publish it only when it is ready to go live.',
      steps: [
        {
          title: 'How do I create a new workflow?',
          items: [
            { text: 'Click "New Workflow" in the top right.' },
            { text: 'Enter a Workflow Name (required) and an optional Description.' },
            { text: 'Click Create Workflow. The workflow is created as a Draft with no stages yet.' },
            { text: 'The Workflow Details page opens automatically — click Add Stage to begin building.', tip: 'Use a clear naming convention such as "Standard – Line Manager" or "Contractors – Direct Approval" so it is obvious which workflow applies to which group.' },
          ],
        },
        {
          title: 'How do I add or edit a stage?',
          items: [
            { text: 'Select a Workflow to edit or start a new workflow. Once you have that workflow open, click Add Stage (or Edit on an existing stage card). A side panel opens with all the stage settings in one place.' },
            { text: 'Enter or edit a Stage Name — use something descriptive like "Line Manager Review" or "Finance Sign-off". Add an optional Description.' },
            { text: 'Select the Approver Type.\n\nor Tick "Prevent self-approval" to stop the approver from approving their own timesheet (strongly recommended).' },
            { text: 'There are options to prevent users from approving their own timesheets and allowing approver to edit timesheet to add more nuance to your processes.' },
            { text: 'Expand the Smart Rules section to configure routing and timeout rules. Save or cancel this screen to return to the Stage section.' },
            { text: 'Click Save to apply the stage.', tip: 'Click "Save & Add Another" to save this stage and immediately open a fresh panel for the next one.' },
          ],
        },
        {
          title: 'How do I configure Smart Rules for a stage?',
          items: [
            { text: 'Expand the Advanced Options section at the bottom of the Stage Panel.' },
            { text: 'Choose between Before Current Stage or After Decision.' },
            { text: 'Configure the Rule. Name the rule, define the condition & threshold value and the Rule Action (what happens when the condition matches or not.' },
            { text: 'Click Save Rule. Add as many rules as needed.', note: 'Rules are evaluated in the order they appear — the first matching rule wins. Use the up/down arrows on each rule card to set priority.' },
          ],
        },
        {
          title: 'How do I reorder stages on a workflow?',
          items: [
            { text: 'Open an Approval Workflow to view the details.' },
            { text: 'In the stage list, click the 3 dot menu on each card, then use the move up and down arrow buttons to change its position.' },
          ],
        },
        {
          title: 'How do I assign a workflow to employees?',
          items: [
            { text: 'From the workflow list, click the Assign button on the workflow row.' },
            { text: 'Select the employees you want to assign.' },
            { text: 'Click Save.', note: 'Each employee can only have one active workflow at a time. Assigning a new workflow automatically replaces their previous one. Only Active workflows can be assigned.' },
          ],
        },
        {
          title: 'How do I activate a workflow?',
          items: [
            { text: 'Click an Approval Workflow to open the Workflow Details page.' },
            { text: 'Check that all stages are fully configured (no stages should be in Draft status).' },
            { text: 'Click the Status button at the top of the page and select Active.', tip: 'Build and test with Draft status. Once Active, any changes will affect employees on their next submission. Consider duplicating the workflow if you need to make significant changes without disrupting live approvals.' },
          ],
        },
      ],
      proTips: [
        'Start with the simplest workflow that meets your requirements — single-stage approvals are easiest to manage and troubleshoot.',
        'Pre-approval rules are powerful for automatically skipping a stage for low-risk timesheets (e.g. under 40 hours). This reduces approver workload significantly.',
        'Keep workflows in Draft until all stages are fully configured and tested — Draft workflows cannot be accidentally assigned to employees.',
        'Duplicate a working workflow before making major changes — you can roll back by reassigning employees to the original.',
      ],
    },
    faq: [
      {
        question: 'What is the difference between Draft, Active, and Inactive?',
        answer: 'Draft — the workflow is being built and cannot be assigned to employees yet. Active — the workflow is live and can be assigned. Inactive — the workflow was previously active but has been decommissioned. Both Draft and Inactive prevent the workflow from being used for new submissions.',
      },
      {
        question: 'What is the difference between a workflow and a stage?',
        answer: 'A workflow is the overall approval process — it has a name, a status, and a list of stages. A stage is one step within that process, such as "Line Manager Review". A timesheet passes through each stage in order until it reaches a "Fully Approved" outcome.',
      },
      {
        question: 'What does "Prevent self-approval" do on a stage?',
        answer: 'If the person who submitted the timesheet is also the designated approver for that stage (for example, a manager approving their own timesheet), the system will skip that stage and pass to the next one. Enabling this setting is strongly recommended to maintain a proper separation of duties.',
      },
      {
        question: 'What happens if a Pre-Approval Rule matches?',
        answer: 'The rule action is applied immediately — depending on what was configured, the stage could continue, be skipped, jumped to a specific stage, or rejected — without the approver being notified. Rules are evaluated in the order they appear; the first match wins.',
      },
      {
        question: 'What happens to timesheets already in progress if I edit a workflow?',
        answer: 'Timesheets already in the approval process continue on the workflow configuration that was in place when they were submitted. The updated workflow applies to new submissions only.',
      },
      {
        question: 'Can I have different workflows for different teams or employee types?',
        answer: 'Yes. Create separate workflows for each group and assign them accordingly. There is no limit to the number of workflows you can have.',
      },
      {
        question: 'Can an approver approve their own timesheet?',
        answer: 'Only if "Prevent self-approval" is not enabled on that stage. If it is enabled (recommended), the system automatically skips the stage when the approver and the submitter are the same person.',
      },
      {
        question: 'What does the visual flow view show?',
        answer: 'The visual flow view on the Workflow Details page renders each stage as a connected node so you can see the approval path at a glance, including how smart routing rules branch between stages. It is a read-only diagram — use the list view to edit stages.',
      },
    ],
    relatedPages: [
      { label: 'Approvals', path: '/approvals' },
      { label: 'My Delegations', path: '/approvals/delegations' },
      { label: 'Delegation Settings', path: '/settings/approvals/delegation' },
      { label: 'User Roles & Permissions', path: '/settings/roles-permissions' },
    ],
  },

  '/settings/roles-permissions/edit/:roleId': {
    pageTitle: 'Edit Role',
    description: 'Configure the name, description, and full permission set for an existing role.',
    icon: Shield,
    functionality: {
      overview:
        'The Edit Role page is where you define exactly what a role can see and do across the application. Permissions are organised into tabs covering Time Tracking, Approvals, Reports, Management, Data & Integrations, and Administration. Each tab shows a count of how many permissions are currently enabled, and you can select all or deselect all within a tab for quick configuration.',
      keyFeatures: [
        { label: 'Role Details', detail: 'Set the role name and an optional description explaining what the role is designed for.' },
        { label: 'Permission Tabs', detail: 'Six tabs group permissions by area: Time Tracking, Approvals, Reports, Management, Data & Integrations, and Administration.' },
        { label: 'Permission Counts', detail: 'Each tab shows a badge with the number of permissions currently enabled, giving you a quick overview of how broad the role is.' },
        { label: 'Select All / Deselect All', detail: 'Each tab has a bulk action to enable or disable all permissions in that area at once.' },
        { label: 'Scope Badges', detail: 'Permission groups are tagged as Basic, Management, Admin, or Advanced so you can understand the level of access being granted.' },
        { label: 'Assigned Users tab', detail: 'Shows all users currently assigned to this role, with options to transfer them to a different role if needed.' },
        { label: 'Save Confirmation', detail: 'When saving an existing role, a summary of changes (added and removed permissions) is shown before you confirm.' },
      ],
      sectionsAndFields: [
        { name: 'Role Name', description: 'The display name of the role as it appears in user assignment dropdowns and the role list.' },
        { name: 'Description', description: 'Optional free text explaining the intended purpose of the role.' },
        { name: 'Time Tracking tab', description: 'Controls dashboard access, timesheet view and edit scope (own / team / all), delete, duplicate, recall, copy from last week, Smart Auto-Fill, and Insights.' },
        { name: 'Approvals tab', description: 'Controls access to the approval dashboard and queue, editing timesheets during approval, rejecting with a reason, bulk approve, delegation, and viewing policy violations and detected issues.' },
        { name: 'Reports tab', description: 'Enables or disables the Timesheet Report, Missing Timesheet Report, and Utilisation Report independently, each with its own view scope and export permission.' },
        { name: 'Management tab', description: 'Controls whether the role can view the employee directory and individual employee details.' },
        { name: 'Data & Integrations tab', description: 'Controls access to view or manage integrations and company settings.' },
        { name: 'Administration tab', description: 'Controls access to each area of Settings, including General Settings (view or configure), Deadlines, Policy Settings, Utilisation Settings, Approval Workflows, Notifications, and Roles & Permissions management.' },
        { name: 'Assigned Users tab', description: 'Lists all users currently on this role. You can transfer individual users to a different role from here.' },
      ],
      useCases: [
        'Granting a new Approver role access to the approval queue, reject, and bulk approve permissions without giving admin access.',
        'Creating a Reports Only role by enabling just the Reports tab permissions and disabling everything else.',
        'Reviewing which permissions are enabled before assigning a role to a new team member.',
        'Narrowing down what a Manager role can see by setting timesheet view scope to "team" rather than "all".',
        'Enabling Smart Auto-Fill and Insights for a senior role while keeping them off for standard users.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Work through each permission tab from left to right — the badges show how many permissions are enabled per tab. Save when done to apply the changes to this role.',
      steps: [
        {
          title: 'How do I configure timesheet access for a role?',
          items: [
            { text: 'Go to the Time Tracking tab.' },
            { text: 'Set View Timesheets Access — choose Own (personal only), Team (department), or All (everyone).' },
            { text: 'Set Edit Timesheets Access to the same scope or narrower.' },
            { text: 'Toggle on Delete, Duplicate, and Recall only if this role requires those actions.', tip: 'Most standard user roles only need Own access. Reserve Team and All for managers and administrators.' },
          ],
        },
        {
          title: 'How do I set up approval permissions?',
          items: [
            { text: 'Go to the Approvals tab.' },
            { text: 'Enable View Approval Dashboard and View Approvals — these are required before any approval actions are visible.' },
            { text: 'Enable Approve, Reject, and Bulk Approve & Reject as needed.' },
            { text: 'Enable Delegate Approval if users in this role should be able to delegate to a colleague while away.', note: 'Compulsory Rejection Reason forces the approver to enter a comment whenever they reject — recommended for audit purposes.' },
          ],
        },
        {
          title: 'How do I enable a report for a role?',
          items: [
            { text: 'Go to the Reports tab.' },
            { text: 'Toggle on the report you want to enable (Timesheet Report, Missing Timesheet Report, or Utilisation Report).' },
            { text: 'Once enabled, set the View Access scope — Own, Team, or All.' },
            { text: 'Toggle Export on if the role should be able to download report data.', tip: 'Each report is independent — you can enable the Timesheet Report without enabling Missing Timesheets.' },
          ],
        },
        {
          title: 'How do I save and apply the changes?',
          items: [
            { text: 'Click Save in the top right when you have finished configuring permissions.' },
            { text: 'A confirmation dialog shows a summary of permissions added and removed.' },
            { text: 'Review the changes and click Confirm to apply.', note: 'Changes take effect immediately. Users assigned to this role may need to refresh their browser to see updated access.' },
          ],
        },
      ],
      proTips: [
        'Use the permission count badges on each tab to quickly spot if a tab has been left unconfigured (0 permissions) or is unusually broad.',
        'The Select All button is useful for admin roles — enable everything, then selectively remove the few permissions that should not apply.',
        'The Assigned Users tab lets you see who will be affected before you save — check this before making significant permission changes to a role with many users.',
        'Scope badges (Basic, Management, Admin, Advanced) help you understand the risk level of each permission group at a glance.',
      ],
    },
    faq: [
      {
        question: 'What is the difference between view scope Own, Team, and All?',
        answer: 'Own restricts the user to seeing only their own data. Team extends access to all employees in the same department as the user. All grants access to every user in the organisation. This applies independently to timesheet viewing, editing, and reporting.',
      },
      {
        question: 'Why are some permission toggles greyed out?',
        answer: 'Some permissions are only available if a related setting has been enabled at the organisation level in General Settings — for example, Smart Auto-Fill and Insights must be turned on globally before they can be granted at the role level.',
      },
      {
        question: 'Can I see what changed before I save?',
        answer: 'Yes. When editing an existing role, clicking Save shows a confirmation dialog with a full list of permissions added and permissions removed since you last saved.',
      },
      {
        question: 'What happens to users assigned to this role when I save changes?',
        answer: 'Changes apply immediately to all users on this role. Users already logged in may need to refresh their browser before the updated permissions take effect.',
      },
      {
        question: 'How do I move a user to a different role from this page?',
        answer: 'Go to the Assigned Users tab, find the user, and use the Transfer option to assign them to a different role.',
      },
    ],
    relatedPages: [
      { label: 'User Roles & Permissions', path: '/settings/roles-permissions' },
      { label: 'Team Members', path: '/team/members' },
      { label: 'General Settings', path: '/settings/general' },
    ],
  },

  '/settings/roles-permissions/create': {
    pageTitle: 'Create Role',
    description: 'Define a new permission profile from scratch or based on a duplicated role.',
    icon: Shield,
    functionality: {
      overview:
        'The Create Role page uses the same editor as Edit Role. You start with a blank slate — enter a name, an optional description, then work through each permission tab to build the profile. If you arrived here via the Duplicate button on an existing role, permissions will already be pre-filled from the source role.',
      keyFeatures: [
        { label: 'Role Name & Description', detail: 'Required before saving — give the role a clear name and describe who it is for.' },
        { label: 'Permission Tabs', detail: 'Six tabs covering Time Tracking, Approvals, Reports, Management, Data & Integrations, and Administration.' },
        { label: 'Pre-filled from Duplicate', detail: 'If created by duplicating an existing role, all permissions from the source are pre-loaded for you to adjust.' },
        { label: 'Select All / Deselect All', detail: 'Quickly bulk-enable or bulk-disable all permissions within a tab.' },
      ],
      sectionsAndFields: [
        { name: 'Role Name', description: 'Required. The display name of the role as it appears in assignment dropdowns.' },
        { name: 'Description', description: 'Optional. A plain-language summary of who the role is designed for.' },
        { name: 'Permission Tabs', description: 'Six tabs — Time Tracking, Approvals, Reports, Management, Data & Integrations, Administration — each grouping related permissions together.' },
      ],
      useCases: [
        'Creating a brand new role for a job function that does not match any existing profile.',
        'Duplicating an existing role as the starting point and then tightening or broadening specific permissions.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Enter a Role Name and description, then move to the permissions tabs to configure exactly what this role can access. Save when done to make the role available to assign to users.',
      steps: [
        {
          title: 'How do I create a role from scratch?',
          items: [
            { text: 'Click Create Role from the User Roles & Permissions page.' },
            { text: 'Enter a Role Name and Description.' },
            { text: 'Work through each permission tab, enabling the permissions the role requires.' },
            { text: 'Click Save. The role is immediately available for assignment to users.', tip: 'Start with the minimum permissions needed. It is easier to grant more access later than to remove permissions users have come to rely on.' },
          ],
        },
        {
          title: 'How do I create a role based on an existing one?',
          items: [
            { text: 'From the User Roles & Permissions list, click Duplicate on the role you want to base it on.' },
            { text: 'The editor opens pre-filled with all permissions from the source role.' },
            { text: 'Update the Role Name and Description to reflect the new role.' },
            { text: 'Adjust permissions as needed, then click Save.' },
          ],
        },
      ],
      proTips: [
        'Use a clear naming convention — e.g. "Approver — Finance" rather than just "Approver" — to make role selection unambiguous when assigning to users.',
        'Fill in the Description field with who the role is for. This saves time when you are assigning roles months later.',
      ],
    },
    faq: [
      {
        question: 'Can I go back and edit a role after creating it?',
        answer: 'Yes. Go to User Roles & Permissions, find the role in the list, and click Edit.',
      },
      {
        question: 'Is the new role available immediately after saving?',
        answer: 'Yes. As soon as you save, the role appears in the role assignment dropdown on Team Members profiles.',
      },
    ],
    relatedPages: [
      { label: 'User Roles & Permissions', path: '/settings/roles-permissions' },
      { label: 'Team Members', path: '/team/members' },
    ],
  },

  '/settings/approvals/delegation': {
    pageTitle: 'Delegation Settings',
    description: 'Configure the organisation-wide rules that govern how approval delegation works for all users.',
    icon: ArrowLeftRight,
    functionality: {
      overview:
        'Delegation Settings gives administrators control over every aspect of the delegation feature at an organisational level. Settings here define who can delegate, how long delegations can last, whether delegates can further delegate, and which notifications are triggered. Changes apply to all users immediately.',
      keyFeatures: [
        { label: 'Master Switch', detail: 'Enables or disables the entire delegation feature for the organisation. When off, managers cannot create delegations and all delegation UI is hidden.' },
        { label: 'Eligibility & Flow', detail: 'Controls who can be a delegate — whether the delegate must already hold approval permissions, and whether self-delegation is allowed.' },
      ],
      sectionsAndFields: [
        { name: 'Enable Approval Delegation toggle', description: 'Master switch. When disabled, no new delegations can be created and all delegation UI is hidden. Existing active delegations are not automatically revoked.' },
        { name: 'Eligibility & Flow section', description: 'Require delegate to have approval permissions; Allow self-delegation.' },
        { name: 'Duration & Overlap Rules section', description: 'Maximum delegation duration (days); Allow overlapping delegations; Allow cascade delegation.' },
        { name: 'Form Requirements section', description: 'Require notes on delegation — forces managers to provide a reason when creating a delegation.' },
        { name: 'Notification Settings section', description: 'Independent toggles for delegation notifications: Created, Started, Expiring Soon, and Revoked.' },
        { name: 'Save Settings button', description: 'Commits all changes. Only enabled when there are unsaved changes.' },
        { name: 'Approvals Settings button', description: 'Navigates to the Approval Workflows page.' },
      ],
      useCases: [
        'Disabling chain delegation to prevent approval chains becoming too long or unaccountable.',
        'Setting a 30-day maximum duration to prevent permanent delegation arrangements.',
        'Requiring administrator approval for all new delegations in a regulated environment.',
        'Turning off delegation entirely during a system migration period.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Start by checking whether the Master Switch is enabled — if delegation is off, no other settings will take effect. Work through each accordion section to configure eligibility, duration limits, form requirements, and notifications. Click Save Settings after making changes in each section.',
      steps: [
        {
          title: 'How do I disable the delegation feature?',
          items: [
            { text: 'Toggle Delegation Enabled to off.' },
            { text: 'Click Save.', note: 'Disabling delegation does not automatically revoke active delegations. Active delegations will continue until they expire. Inform affected users before disabling.' },
          ],
        },
        {
          title: 'How do I set a maximum delegation duration?',
          items: [
            { text: 'Find the Max Duration field in the Duration & Overlap Rules section.' },
            { text: 'Enter the maximum number of calendar days allowed for any single delegation.' },
            { text: 'Click Save.', note: 'This setting applies to new delegations only. Existing delegations are not truncated.' },
          ],
        },
        {
          title: 'How do I require administrator approval for delegations?',
          items: [
            { text: 'Toggle Require Admin Approval to on.' },
            { text: 'Click Save.' },
            { text: 'From this point, all new delegations will enter a Pending Approval state until an administrator approves them.', tip: 'Ensure at least two administrators are active so delegation approvals are not blocked if one is unavailable.' },
          ],
        },
      ],
      proTips: [
        'Set the advance notice requirement to at least 1 day to prevent last-minute delegations that give delegates insufficient preparation time.',
        'Disable chain delegation unless your organisation specifically requires it — it simplifies the approval audit trail considerably.',
        'Review the audit log quarterly to check for unusual delegation patterns such as the same person being delegated to repeatedly.',
        'If you enable Require Admin Approval, set up a shared inbox or notification so approval requests are not missed.',
      ],
    },
    faq: [
      {
        question: 'What is chain delegation?',
        answer: 'Chain delegation is when a delegate creates their own delegation to pass approval responsibilities to a third person. Disabling chain delegation means a delegate must handle the approvals themselves.',
      },
      {
        question: 'What happens to active delegations if I reduce the Max Duration setting?',
        answer: 'Existing active delegations are not affected by changing the Max Duration — they continue until their original end date. The new limit applies to delegations created after the change.',
      },

      {
        question: 'How does Require Admin Approval affect users?',
        answer: 'When enabled, a delegation created by a user enters Pending Approval status and is not active until an administrator approves it. The delegate does not receive approval responsibilities until the delegation is approved.',
      },
    ],
    relatedPages: [
      { label: 'My Delegations', path: '/approvals/delegations' },
      { label: 'Approval Workflows', path: '/settings/approvals' },
      { label: 'Notifications', path: '/settings/notifications' },
      { label: 'Approvals', path: '/approvals' },
    ],
  },

  '/settings/approvals/delegation/audit': {
    pageTitle: 'Delegation Audit Report',
    description: 'A read-only report showing all delegation activity across the organisation, accessible to administrators only.',
    icon: ClipboardCheck,
    functionality: {
      overview:
        'The Delegation Audit Report gives administrators a complete, filterable view of every delegation that has been created across the organisation — past and present. Each row shows who delegated, who received the delegation, the date range, status, and any notes or revoke reasons. The report is read-only; changes to delegations are made from the Delegation Settings or My Delegations pages.',
      keyFeatures: [
        { label: 'Search', detail: 'Filter the list by manager name or delegate name to find a specific record quickly.' },
        { label: 'Grouping & Sorting', detail: 'Group by Manager, Delegate, or Status to spot patterns or repeated delegations.' },
        { label: 'Export CSV', detail: 'Downloads the full audit log to CSV for record-keeping or compliance purposes.' },
        { label: 'Refresh', detail: 'Reloads the data from the server to pick up any recently created or updated delegations.' },
      ],
      sectionsAndFields: [
        { name: 'Manager column', description: 'The user who created the delegation and handed over their approval authority.' },
        { name: 'Delegate column', description: 'The user who received the approval authority for the delegation period.' },
        { name: 'Date Range column', description: 'The start and end dates of the delegation, with the total duration in days shown in brackets.' },
        { name: 'Status column', description: 'Current state — Active means the delegation is in effect right now; Expired means it ended naturally; Revoked means it was cancelled early.' },
        { name: 'Notes column', description: 'Optional notes entered by the manager when the delegation was created, typically the reason for the absence.' },
        { name: 'Revoke Reason column', description: 'The reason entered when a delegation was cancelled before its end date. Blank for Expired or Active records.' },
        { name: 'Search box', description: 'Searches across the Manager and Delegate name columns in real time.' },
        { name: 'Export CSV button', description: 'Downloads all records currently loaded (not just the current page) to a CSV file.' },
        { name: 'Refresh button', description: 'Reloads the delegation data from the server.' },
      ],
      useCases: [
        'Confirming that approval cover was in place for a specific manager during a period of absence.',
        'Investigating an approval that was made by an unexpected approver — checking whether a delegation was active at the time.',
        'Running a quarterly review of delegation patterns to identify managers who delegate frequently or to the same person repeatedly.',
        'Exporting delegation history to CSV for a compliance or HR audit.',
        'Identifying delegations that were revoked early and reviewing the reason recorded.',
      ],
    },
    userGuide: {
      gettingStarted:
        'All delegation activity loads automatically. Use the search box to find a specific manager or delegate by name. Apply status or date filters to narrow the results, then export to CSV if you need to share or archive the records.',
      steps: [
        {
          title: 'How do I find all delegations for a specific manager?',
          items: [
            { text: 'Open the search box at the top of the table.' },
            { text: 'Type the manager\'s name — the list filters in real time to show only rows where they appear as Manager or Delegate.' },

          ],
        },
        {
          title: 'How do I export the audit log?',
          items: [
            { text: 'Click Export CSV in the toolbar at the top right.' },
            { text: 'A CSV file containing all loaded records is downloaded immediately — including Manager, Delegate, Date Range, Duration, Status, Notes, and Revoke Reason.' },
            { text: 'The export is not limited to the current page — it includes all records.' },
          ],
        },
        {
          title: 'How do I check if a delegation was active on a specific date?',
          items: [
            { text: 'Search for the manager\'s name to narrow the list.' },
            { text: 'Review the Date Range column — check whether your target date falls between the Start and End dates.' },
            { text: 'Check the Status column — Active or Expired both mean the delegation ran its course; Revoked means it was cancelled and may not have covered the full range.' },
          ],
        },
      ],
      proTips: [
        'The Revoke Reason column is only populated for Revoked records — if it is blank on a Revoked row, no reason was entered when it was cancelled.',
        'Export the CSV before any system changes or at month-end to maintain a local record outside the application.',
        'If you need to make changes to a delegation, go to My Delegations (for the manager\'s own delegations) or contact the manager directly — this page is read-only.',
      ],
    },
    faq: [
      {
        question: 'Can I edit or delete a delegation from this page?',
        answer: 'No. The Delegation Audit Report is read-only. To revoke an active delegation, the manager should go to My Delegations. Administrators cannot revoke delegations on behalf of managers from this screen.',
      },

      {
        question: 'What is the difference between Expired and Revoked?',
        answer: 'Expired means the delegation reached its natural end date and ended automatically. Revoked means a manager (or administrator) cancelled it before the end date. Both statuses mean the delegation is no longer active.',
      },
      {
        question: 'How far back does the audit log go?',
        answer: 'The audit log shows all delegations ever created in the system — there is no cut-off date. Use the search or grouping options to narrow down older records.',
      },
    ],
    relatedPages: [
      { label: 'Delegation Settings', path: '/settings/approvals/delegation' },
      { label: 'My Delegations', path: '/approvals/delegations' },
      { label: 'Approval Workflows', path: '/settings/approvals' },
    ],
  },
};

// Helper: look up help content for a given path.
// Tries an exact match first, then walks up the path replacing the last
// segment with a wildcard pattern so dynamic routes (e.g. /edit/:id) resolve
// to their registered key (e.g. /settings/roles-permissions/edit/:roleId).
export const getHelpContent = (path: string): HelpContent | null => {
  if (helpContent[path]) return helpContent[path];

  // Build a list of candidate keys by progressively replacing the last
  // path segment with each registered key's last segment.
  const segments = path.split('/').filter(Boolean);
  const registeredKeys = Object.keys(helpContent);

  for (let i = segments.length; i > 0; i--) {
    const prefix = '/' + segments.slice(0, i - 1).join('/');
    const match = registeredKeys.find(key => {
      const keySegs = key.split('/').filter(Boolean);
      if (keySegs.length !== i) return false;
      return keySegs.every((seg, idx) =>
        seg.startsWith(':') || seg === segments[idx]
      );
    });
    if (match) return helpContent[match];
  }

  return null;
};
